import React, { useState, useContext, useEffect, useRef } from "react";

//MUI components
import {
    Dialog as MuiDialog,
    DialogTitle as MuiDialogTitle,
    DialogContent as MuiDialogContent,
    DialogActions as MuiDialogActions,
    IconButton,
    CircularProgress,
    Grid,
    TextField,
    MenuItem,
    Breadcrumbs,
    Button as MuiButton,
    Tooltip,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    Chip,
    Switch,
    FormHelperText,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";

//Custom portal components
import Table from "../common/Tables/StyledTable";
import { getCampaignList, whatsAppCampaignActions, getCampaignCatalogs, checkCampaignName, createCampaign, activateCampaign, deactivateCampaign, deleteCampaign, updateCampaignSchedule, previewSegment } from "../DataFunctions";
import AppContext from "../../Context/AppContext";
import RefreshButton from "../common/RefreshButton";
import CustomButton from "../common/CustomButton";
import SearchBox from "../common/SearchBox";
import { AgeFilter, SingleSelectFilter, MultiSelectFilter } from "./SegmentationFilters";
import CreateNewTemplateForm from "./CreateNewTemplateForm";
import CustomModalActions from "../common/Modals/CustomModalActions";
import FooterControls from "../FooterComponents/FooterControls";

//External libraries
import Swal from "sweetalert2";
import Axios from "axios";

//Icons
import CloseIcon from "@material-ui/icons/Close";
import AgregarIcono from "../../Icons/AgregarIcono";
import EditarIcono from "../../Icons/EditarIcono";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

//styleSheets
import "./Styles/uploadCampaingsPhoneNumbers.css";

const Dialog = withStyles((theme) => ({
    root: {
        marginTop: "56px",
        [theme.breakpoints.up("md")]: {
            marginTop: "64px",
        },
    },
}))(MuiDialog);

const DialogTitle = withStyles((theme) => ({
    root: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid #e3e3e3",
        padding: "8px 20px",
    },
}))(MuiDialogTitle);

const DialogContent = withStyles((theme) => ({
    root: {
        paddingTop: "15px",
        paddingRight: "10px",
        paddingLeft: "10px",
    },
}))(MuiDialogContent);

const Button = withStyles((theme) => ({
    root: {
        fontWeight: "400",
    },
    label: {
        textTransform: "none",
        fontSize: "1rem",
    },
}))(MuiButton);

const campaignColumns = [
    { key: "name", label: "Nombre" },
    { key: "template", label: "Plantilla" },
    { key: "sending", label: "Tipo", catalog: "Campaign_Sending" },
    { key: "delivery", label: "Enviada a", catalog: "Campaign_Delivery" },
    { key: "date", label: "Fecha creación", formatDate: true },
    { key: "user", label: "Usuario creador" },
];

// Reglas de acciones por estado
const campaignStateRules = {
    ACTIVE: { canActivate: false, canDeactivate: true, canDelete: true, canReprogram: true },
    INACTIVE: { canActivate: true, canDeactivate: false, canDelete: true, canReprogram: false },
    EXECUTED: { canActivate: false, canDeactivate: false, canDelete: false, canReprogram: false },
    DELETE: { canActivate: false, canDeactivate: false, canDelete: false, canReprogram: false },
};

const getStateColor = (state) => {
    switch (state) {
        case "ACTIVE": return "#44AB3F";
        case "INACTIVE": return "#e48922";
        case "EXECUTED": return "#27315d";
        case "DELETE": return "#E42922";
        default: return "#B2B2B2";
    }
};

const getStateLabel = (state) => {
    switch (state) {
        case "ACTIVE": return "Activa";
        case "INACTIVE": return "Inactiva";
        case "EXECUTED": return "Ejecutada";
        case "DELETE": return "Eliminada";
        default: return state || "";
    }
};

// Capitaliza la primera letra de un texto
const capitalizeFirst = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
};

// Renderiza un valor de texto (intereses/intención) como lista vertical sin viñetas
// para evitar que la tabla crezca horizontalmente. Cada item con la primera letra en mayúscula.
const renderCellList = (value) => {
    if (!value) return "";
    // Soporta arrays o strings separados por coma
    let items = Array.isArray(value)
        ? value
        : String(value).split(",").map((v) => v.trim()).filter(Boolean);
    if (items.length === 0) return "";
    return (
        <div>
            {items.map((item, i) => (
                <div key={i} style={{ marginBottom: "2px" }}>{capitalizeFirst(item)}</div>
            ))}
        </div>
    );
};

const CampaignsDialog = (props) => {
    const [state, setState] = useState({
        campaignList: [],
        Loading: false,
        errorInFetch: false,
        statusFilter: "ACTIVE",
        campaignSearchQuery: "",
        // Form state
        section: "list", // 'list', 'newCampaign', 'segmentation', 'scheduling', 'selectTemplate', 'campaignDetail'
        campaignName: "",
        nameAvailable: null, // null = not checked, true = available, false = taken
        checkingName: false,
        selectedTemplate: "",
        templateParams: [], // [{ param: "param1", source: "" }, ...]
        selectedCampaign: null,
        editingSchedule: false,
        switchActive: false,
        templateList: [],
        templateFullList: [],
        multiMediaURLArray: [],
        loadingTemplates: false,
        openNewTemplateForm: false,
        templateSearchQuery: "",
        templateDialog: {
            openDialogActions: false,
            dialogMessage: "",
            dialogAction: "",
            dialogFunction: "",
        },
        deliveryType: "", // 'ALL', 'SEGMENTED', etc.
        sendingType: "", // 'INMEDIATE' or 'SCHEDULED'
        // Scheduling state
        startDate: "",
        startTime: "",
        recurrent: false,
        frequency: "DAILY",
        endDate: "",
        daysOfWeek: [],
        sendOnce: false,
        // Segmentation state
        segFilters: {
            age: { operator: "", ageFrom: "", ageTo: "" },
            gender: [],
            state: [],
            locality: [],
            interests: [],
            intention: [],
            sentiment: "",
            interactions: { operator: "", from: "", to: "" },
            lastModifiedDate: { type: "", from: "", to: "" },
        },
        segCatalogOptions: {
            age: [],
            gender: [],
            sentiment: [],
            daysOfWeek: [],
        },
        catalogTranslations: {
            Campaign_Sending: {},
            Campaign_Delivery: {},
        },
        segDynamicOptions: {
            state: [],
            locality: [],
            interests: [],
            intention: [],
        },
        loadingSegOptions: false,
        segmentPreview: null,
        totalContacts: 0,
        segPage: 1,
        segPageSize: 100,
        segTotalPages: 0,
        segContacts: [],
        loadingPreview: false,
        loadingTable: false,
        segOperators: {
            gender: false,
            state: false,
            locality: false,
            interests: false,
            intention: false,
        },
        errors: {
            campaignName: false,
            selectedTemplate: false,
            startDate: false,
            startTime: false,
            endDate: false,
            daysOfWeek: false,
        },
    });

    const context = useContext(AppContext);
    const { updateContextAttribute } = context;

    const { openCampaignsDialog, handleOpenCampaignsDialog, embedded, currentTab } = props;

    const {
        campaignList,
        Loading,
        errorInFetch,
        statusFilter,
        campaignSearchQuery,
        section,
        campaignName,
        nameAvailable,
        checkingName,
        selectedTemplate,
        templateParams,
        selectedCampaign,
        editingSchedule,
        switchActive,
        templateList,
        templateFullList,
        multiMediaURLArray,
        loadingTemplates,
        openNewTemplateForm,
        templateSearchQuery,
        templateDialog,
        deliveryType,
        sendingType,
        startDate,
        startTime,
        recurrent,
        frequency,
        endDate,
        daysOfWeek,
        sendOnce,
        segFilters,
        segCatalogOptions,
        catalogTranslations,
        segDynamicOptions,
        loadingSegOptions,
        segmentPreview,
        totalContacts,
        segPage,
        segPageSize,
        segTotalPages,
        segContacts,
        loadingPreview,
        loadingTable,
        segOperators,
        errors,
    } = state;

    useEffect(() => {
        if (openCampaignsDialog) {
            fetchCampaignList();
            fetchCampaignCatalogs();
        }
    }, [openCampaignsDialog]);

    // Resetear a lista cuando se cambia al tab de campañas
    useEffect(() => {
        if (embedded && currentTab === 1) {
            setState((prev) => ({ ...prev, section: "list" }));
        }
    }, [currentTab]);

    const fetchCampaignList = async () => {
        setState((prev) => ({
            ...prev,
            Loading: true,
            errorInFetch: false,
        }));
        try {
            let res = await getCampaignList();
            let data = res?.data;

            let campaigns = null;
            let responseCode = data?.response?.code ? parseInt(data.response.code) : null;

            // Handle response codes (same pattern as templates)
            if (responseCode === 6) {
                // No campaigns available
                setState((prev) => ({
                    ...prev,
                    campaignList: [],
                    Loading: false,
                    errorInFetch: false,
                }));
                return;
            }

            if (Array.isArray(data)) {
                campaigns = data;
            } else if (data?.response?.templateList && Array.isArray(data.response.templateList)) {
                campaigns = data.response.templateList;
            } else if (data?.body?.response?.templateList && Array.isArray(data.body.response.templateList)) {
                campaigns = data.body.response.templateList;
            } else if (data?.response?.data && Array.isArray(data.response.data)) {
                campaigns = data.response.data;
            } else if (data?.templateList && Array.isArray(data.templateList)) {
                campaigns = data.templateList;
            } else if (data?.response && Array.isArray(data.response)) {
                campaigns = data.response;
            }

            if (campaigns !== null) {
                setState((prev) => ({
                    ...prev,
                    campaignList: campaigns,
                    Loading: false,
                    errorInFetch: false,
                }));
            } else {
                process.env.REACT_APP_DEBUG === "true" && console.log("getCampaignList: could not parse campaigns from data:", data);
                setState((prev) => ({
                    ...prev,
                    campaignList: [],
                    Loading: false,
                    errorInFetch: false,
                }));
            }
        } catch (error) {
            setState((prev) => ({
                ...prev,
                campaignList: [],
                Loading: false,
                errorInFetch: true,
            }));
        }
    };

    const fetchTemplateList = async () => {
        setState((prev) => ({ ...prev, loadingTemplates: true }));
        try {
            let res = await whatsAppCampaignActions(null, "getTemplateList");
            let responseCode = res?.data?.response?.code ? parseInt(res.data.response.code) : 99;
            if (responseCode === 5) {
                let templates = res.data.response.templateList
                    .filter((template) => template.templateStatus !== "PENDING_DELETION");
                let templateNames = templates.map((template) => template.templateName);

                // Lazy load multimedia
                let multiMediaURLArrayNew = [];
                for (let i = 0; i < templates.length; i++) {
                    let multimediaUrl = templates[i].multimediaUrl;
                    if (multimediaUrl) {
                        try {
                            let mediaRes = await Axios({ method: "GET", url: multimediaUrl, responseType: "blob" });
                            let blobUrl = URL.createObjectURL(mediaRes.data);
                            multiMediaURLArrayNew.push({ URL: blobUrl, type: mediaRes.data.type });
                        } catch (e) {
                            multiMediaURLArrayNew.push({ URL: "", type: "" });
                        }
                    } else {
                        multiMediaURLArrayNew.push({ URL: "", type: "" });
                    }
                }

                setState((prev) => ({
                    ...prev,
                    templateList: templateNames,
                    templateFullList: templates,
                    multiMediaURLArray: multiMediaURLArrayNew,
                    loadingTemplates: false,
                }));
            } else {
                setState((prev) => ({
                    ...prev,
                    templateList: [],
                    templateFullList: [],
                    multiMediaURLArray: [],
                    loadingTemplates: false,
                }));
            }
        } catch (error) {
            setState((prev) => ({
                ...prev,
                templateList: [],
                templateFullList: [],
                multiMediaURLArray: [],
                loadingTemplates: false,
            }));
        }
    };

    const openNewCampaignForm = () => {
        fetchTemplateList();
        fetchCampaignCatalogs();
        setState((prev) => ({
            ...prev,
            section: "newCampaign",
            campaignName: "",
            nameAvailable: null,
            checkingName: false,
            selectedTemplate: "",
            templateParams: [],
            selectedCampaign: null,
            deliveryType: "",
            sendingType: "",
            errors: { campaignName: false, selectedTemplate: false },
        }));
    };

    const openCampaignDetail = (campaign) => {
        // Parse schedule data from campaign
        let parsedStartDate = "";
        let parsedStartTime = "";
        let parsedEndDate = "";
        let parsedRecurrent = false;
        let parsedFrequency = "DAILY";
        let parsedDaysOfWeek = [];

        if (campaign.schedule) {
            const schedule = campaign.schedule;
            // Parse startDate ISO to date and time — para RECURRING usa startDate, para ONE_TIME usa executionDateTime
            const dateSource = schedule.startDate || schedule.executionDateTime;
            if (dateSource) {
                const startDt = new Date(dateSource);
                parsedStartDate = startDt.toISOString().split("T")[0]; // "2026-07-13"
                parsedStartTime = startDt.toTimeString().slice(0, 5);  // "15:00"
            }
            // Parse endDate ISO to date
            if (schedule.endDate) {
                const endDt = new Date(schedule.endDate);
                parsedEndDate = endDt.toISOString().split("T")[0];
            }
            // Parse recurrence
            parsedRecurrent = schedule.type === "RECURRING";
            // Parse rrule
            if (schedule.rrule) {
                if (schedule.rrule.includes("FREQ=WEEKLY")) {
                    parsedFrequency = "WEEKLY";
                    // Extract BYDAY if present
                    const byDayMatch = schedule.rrule.match(/BYDAY=([A-Z,]+)/);
                    if (byDayMatch) {
                        const dayMap = { MO: "MONDAY", TU: "TUESDAY", WE: "WEDNESDAY", TH: "THURSDAY", FR: "FRIDAY", SA: "SATURDAY", SU: "SUNDAY" };
                        parsedDaysOfWeek = byDayMatch[1].split(",").map((d) => dayMap[d] || d).filter(Boolean);
                    }
                } else {
                    parsedFrequency = "DAILY";
                }
            }
        }

        setState((prev) => ({
            ...prev,
            section: "campaignDetail",
            selectedCampaign: campaign,
            editingSchedule: false,
            switchActive: campaign.state === "ACTIVE",
            startDate: parsedStartDate,
            startTime: parsedStartTime,
            recurrent: parsedRecurrent,
            frequency: parsedFrequency,
            endDate: parsedEndDate,
            daysOfWeek: parsedDaysOfWeek,
        }));
        fetchCampaignCatalogs();
    };

    const backToList = () => {
        setState((prev) => ({
            ...prev,
            section: "list",
            campaignName: "",
            selectedTemplate: "",
            templateParams: [],
            deliveryType: "",
            sendingType: "",
            startDate: "",
            startTime: "",
            recurrent: false,
            frequency: "DAILY",
            endDate: "",
            daysOfWeek: [],
            errors: { campaignName: false, selectedTemplate: false, startDate: false, startTime: false, endDate: false, daysOfWeek: false },
        }));
    };

    const backToNewCampaign = () => {
        setState((prev) => ({
            ...prev,
            section: "newCampaign",
        }));
    };

    // Helpers para alertas de confirmación
    const confirmCancel = async (haChanges, onConfirm) => {
        if (!haChanges) { onConfirm(); return; }
        let result = await Swal.fire({
            title: "¿Estás seguro que deseas cancelar tus cambios?",
            text: "Si confirmas se perderán todos los cambios realizados.",
            showDenyButton: true,
            confirmButtonText: "Sí",
            denyButtonText: "No",
            confirmButtonColor: "#27315d",
            denyButtonColor: "#27315d",
        });
        if (result.isConfirmed) onConfirm();
    };

    const confirmExit = async (hasChanges, onConfirm) => {
        if (!hasChanges) { onConfirm(); return; }
        let result = await Swal.fire({
            title: "¿Estás seguro que deseas salir?",
            text: "Se perderán los cambios realizados.",
            showDenyButton: true,
            confirmButtonText: "Sí",
            denyButtonText: "No",
            confirmButtonColor: "#27315d",
            denyButtonColor: "#27315d",
        });
        if (result.isConfirmed) onConfirm();
    };

    const cancelSegmentation = () => {
        setState((prev) => ({
            ...prev,
            section: "newCampaign",
            deliveryType: "",
            segFilters: {
                age: { operator: "", ageFrom: "", ageTo: "" },
                gender: [],
                state: [],
                locality: [],
                interests: [],
                intention: [],
                sentiment: "",
                interactions: { operator: "", from: "", to: "" },
                lastModifiedDate: { type: "", from: "", to: "" },
            },
            segmentPreview: null,
        }));
    };

    const cancelScheduling = () => {
        setState((prev) => ({
            ...prev,
            section: "newCampaign",
            sendingType: "",
            startDate: "",
            startTime: "",
            recurrent: false,
            frequency: "DAILY",
            endDate: "",
            daysOfWeek: [],
        }));
    };

    const clearSchedulingFields = () => {
        setState((prev) => ({
            ...prev,
            startDate: "",
            startTime: "",
            recurrent: false,
            frequency: "DAILY",
            endDate: "",
            daysOfWeek: [],
        }));
    };

    const openSegmentation = () => {
        setState((prev) => ({
            ...prev,
            section: "segmentation",
            segPage: 1,
            segFilters: {
                age: { operator: "", ageFrom: "", ageTo: "" },
                gender: [],
                state: [],
                locality: [],
                interests: [],
                intention: [],
                sentiment: "",
                interactions: { operator: "", from: "", to: "" },
                lastModifiedDate: { type: "", from: "", to: "" },
            },
            segmentPreview: null,
            totalContacts: 0,
            segContacts: [],
            segTotalPages: 0,
            segOperators: { gender: false, state: false, locality: false, interests: false, intention: false },
            loadingPreview: true,
        }));
        fetchCampaignCatalogs();
        fetchSegmentPreview({
            age: { operator: "", ageFrom: "", ageTo: "" },
            gender: [],
            state: [],
            locality: [],
            interests: [],
            intention: [],
            sentiment: "",
            interactions: "",
            lastModifiedDate: "",
        }, 1);
    };

    const fetchCampaignCatalogs = async () => {
        setState((prev) => ({ ...prev, loadingSegOptions: true }));
        try {
            let res = await getCampaignCatalogs();
            let data = res?.data;
            let catalogs = data?.response?.catalogs || [];
            let dynamicCatalogs = data?.response?.dynamicCatalogs || {};
            process.env.REACT_APP_DEBUG === "true" && console.log("dynamicCatalogs keys:", Object.keys(dynamicCatalogs), dynamicCatalogs);

            let genderCatalog = catalogs.find((c) => c.name === "Gender");
            let sentimentCatalog = catalogs.find((c) => c.name === "Sentiment");
            let ageCatalog = catalogs.find((c) => c.name === "Age");
            let dayOfWeekCatalog = catalogs.find((c) => c.name === "Day_Of_Week");
            let sendingCatalog = catalogs.find((c) => c.name === "Campaign_Sending");
            let deliveryCatalog = catalogs.find((c) => c.name === "Campaign_Delivery");
            let estateCatalog = catalogs.find((c) => c.name === "Estate" || c.name === "estate" || c.name === "State");
            process.env.REACT_APP_DEBUG === "true" && console.log("estateCatalog:", estateCatalog);
            process.env.REACT_APP_DEBUG === "true" && console.log("dynamicCatalogs.estate:", dynamicCatalogs.estate);

            // Build translation maps: { value: descriptionES }
            let sendingTranslations = {};
            if (sendingCatalog?.catalog) {
                sendingCatalog.catalog.forEach((item) => { sendingTranslations[item.value] = item.descriptionES; });
            }
            let deliveryTranslations = {};
            if (deliveryCatalog?.catalog) {
                deliveryCatalog.catalog.forEach((item) => { deliveryTranslations[item.value] = item.descriptionES; });
            }

            setState((prev) => ({
                ...prev,
                segCatalogOptions: {
                    age: ageCatalog?.catalog?.map((item) => ({ value: item.value, label: item.descriptionES })) || [],
                    gender: genderCatalog?.catalog?.map((item) => ({ value: item.value, label: item.descriptionES })) || [],
                    sentiment: sentimentCatalog?.catalog?.map((item) => ({ value: item.value, label: item.descriptionES })) || [],
                    daysOfWeek: dayOfWeekCatalog?.catalog?.map((item) => ({ value: item.value, label: item.descriptionES })) || [],
                },
                catalogTranslations: {
                    Campaign_Sending: sendingTranslations,
                    Campaign_Delivery: deliveryTranslations,
                },
                segDynamicOptions: {
                    state: dynamicCatalogs.states || [],
                    locality: dynamicCatalogs.localities || [],
                    interests: dynamicCatalogs.interests || [],
                    intention: dynamicCatalogs.intents || [],
                },
                loadingSegOptions: false,
            }));
        } catch (error) {
            console.log("Error fetching campaign catalogs:", error);
            setState((prev) => ({ ...prev, loadingSegOptions: false }));
        }
    };

    const openScheduling = () => {
        setState((prev) => ({
            ...prev,
            section: "scheduling",
        }));
        fetchCampaignCatalogs();
    };

    const openSelectTemplate = () => {
        if (templateFullList.length === 0) {
            fetchTemplateList();
        }
        setState((prev) => ({
            ...prev,
            section: "selectTemplate",
        }));
    };

    const handleSelectTemplateFromList = (templateName) => {
        // Only store the selection, don't navigate back yet
        setState((prev) => ({
            ...prev,
            selectedTemplate: templateName,
        }));
    };

    const confirmSelectTemplate = () => {
        // Navigate back to new campaign with selected template and detect params
        const template = templateFullList.find((t) => t.templateName === selectedTemplate);
        let detectedParams = [];
        if (template) {
            const content = template.templateContent || template.templateBody || "";
            const regex = /\{\{(\d+)\}\}/g;
            let match;
            const paramNumbers = new Set();
            while ((match = regex.exec(content)) !== null) {
                paramNumbers.add(parseInt(match[1]));
            }
            detectedParams = Array.from(paramNumbers)
                .sort((a, b) => a - b)
                .map((num) => ({ param: `param${num}`, source: "" }));
        }
        setState((prev) => ({
            ...prev,
            section: "newCampaign",
            templateParams: detectedParams,
        }));
    };

    const cancelSelectTemplate = () => {
        // Go back without keeping the selection (if user cancels)
        confirmAndNavigate(backToNewCampaign);
    };

    const handleOpenNewTemplateForm = (open) => {
        setState((prev) => ({ ...prev, openNewTemplateForm: open }));
    };

    const handleTemplateDialogAction = (openDialog, message, action, dialogFunction) => {
        setState((prev) => ({
            ...prev,
            templateDialog: {
                openDialogActions: openDialog,
                dialogMessage: message,
                dialogAction: action,
                dialogFunction: dialogFunction,
            },
        }));
    };

    const refreshTemplateListForCampaign = async () => {
        await fetchTemplateList();
        // Ensure we stay in selectTemplate section after refresh
        setState((prev) => ({ ...prev, section: "selectTemplate" }));
    };

    const onClose = () => {
        setState((prev) => ({
            ...prev,
            section: "list",
            campaignName: "",
            selectedTemplate: "",
            deliveryType: "",
            sendingType: "",
            startDate: "",
            startTime: "",
            recurrent: false,
            frequency: "DAILY",
            endDate: "",
            daysOfWeek: [],
            errors: { campaignName: false, selectedTemplate: false, startDate: false, startTime: false, endDate: false, daysOfWeek: false },
        }));
        handleOpenCampaignsDialog(false);
    };

    const hasUnsavedSchedulingChanges = () => {
        if (section === "scheduling") {
            return startDate !== "" || startTime !== "" || recurrent || endDate !== "" || daysOfWeek.length > 0;
        }
        if (section === "segmentation") {
            return segFilters.age.operator !== "" || segFilters.gender.length > 0 ||
                segFilters.state.length > 0 || segFilters.locality.length > 0 ||
                segFilters.interests.length > 0 || segFilters.intention.length > 0 ||
                segFilters.sentiment !== "" || segFilters.interactions.operator !== "" ||
                segFilters.lastModifiedDate.type !== "";
        }
        return false;
    };

    const [confirmDialog, setConfirmDialog] = useState({ open: false, onConfirm: null });

    const confirmAndNavigate = (navigateFn) => {
        if ((section === "scheduling" || section === "segmentation") && hasUnsavedSchedulingChanges()) {
            setConfirmDialog({ open: true, onConfirm: navigateFn });
        } else if (section === "newCampaign" && hasNewCampaignChanges()) {
            setConfirmDialog({ open: true, onConfirm: navigateFn });
        } else {
            navigateFn();
        }
    };

    const handleCampaignNameChange = (value) => {
        setState((prev) => ({
            ...prev,
            campaignName: value,
            nameAvailable: null,
            checkingName: false,
            errors: { ...prev.errors, campaignName: value.trim() === "" },
        }));

        // Debounce the name check
        if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
        if (value.trim() === "") return;

        nameCheckTimer.current = setTimeout(async () => {
            setState((prev) => ({ ...prev, checkingName: true }));
            try {
                let res = await checkCampaignName(value.trim());
                let available = res?.data?.response?.available ?? res?.data?.available ?? null;
                setState((prev) => ({
                    ...prev,
                    nameAvailable: available,
                    checkingName: false,
                }));
            } catch (error) {
                setState((prev) => ({ ...prev, checkingName: false, nameAvailable: null }));
            }
        }, 500);
    };

    const nameCheckTimer = useRef(null);

    const handleTemplateChange = (value) => {
        // Detect template parameters {{1}}, {{2}}, etc.
        let detectedParams = [];
        const template = templateFullList.find((t) => t.templateName === value);
        if (template) {
            const content = template.templateContent || template.templateBody || template.body || "";
            const regex = /\{\{(\d+)\}\}/g;
            let match;
            const paramNumbers = new Set();
            while ((match = regex.exec(content)) !== null) {
                paramNumbers.add(parseInt(match[1]));
            }
            detectedParams = Array.from(paramNumbers)
                .sort((a, b) => a - b)
                .map((num) => ({ param: `param${num}`, source: "" }));
        }
        console.log("handleTemplateChange:", value, "templateFullList length:", templateFullList.length, "template found:", !!template, "content:", template?.templateContent?.substring(0, 50), "params:", detectedParams);
        setState((prev) => ({
            ...prev,
            selectedTemplate: value,
            templateParams: detectedParams,
            errors: { ...prev.errors, selectedTemplate: value === "" },
        }));
    };

    const handleDeliveryTypeChange = (value) => {
        setState((prev) => ({
            ...prev,
            deliveryType: value,
        }));
        if (value === "SEGMENTED") {
            openSegmentation();
        }
    };

    const handleSendingTypeChange = (value) => {
        setState((prev) => ({
            ...prev,
            sendingType: value,
        }));
        if (value === "SCHEDULED") {
            openScheduling();
        }
    };

    // ==================== RENDER LIST VIEW ====================
    const statusOptions = [
        { value: "ALL", label: "Todas" },
        { value: "EXECUTED", label: "Ejecutadas" },
        { value: "DELETE", label: "Eliminadas" },
        { value: "ACTIVE", label: "Activas" },
        { value: "INACTIVE", label: "Inactivas" },
    ];

    const handleStatusFilterChange = (value) => {
        setState((prev) => ({ ...prev, statusFilter: value }));
    };

    const searchCampaigns = (query) => {
        setState((prev) => ({ ...prev, campaignSearchQuery: query }));
    };

    const getFilteredCampaigns = () => {
        let filtered = campaignList;
        if (statusFilter !== "ALL") {
            filtered = filtered.filter((campaign) => {
                const state = (campaign.state || "").toUpperCase();
                return state === statusFilter;
            });
        }
        if (campaignSearchQuery) {
            const lower = campaignSearchQuery.toLowerCase();
            filtered = filtered.filter((campaign) =>
                (campaign.name || "").toLowerCase().includes(lower)
            );
        }
        return filtered;
    };

    const renderListView = () => {
        const filteredCampaigns = getFilteredCampaigns();
        return (
        <React.Fragment>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", gap: "15px" }}>
                <TextField
                    select
                    value={statusFilter}
                    variant="outlined"
                    size="small"
                    style={{ minWidth: "180px" }}
                    SelectProps={{
                        MenuProps: {
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            transformOrigin: { vertical: "top", horizontal: "left" },
                            getContentAnchorEl: null,
                        },
                    }}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                    {statusOptions.map((opt, i) => (
                        <MenuItem key={i} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </TextField>
                <SearchBox searchFunction={searchCampaigns} searchQuery={campaignSearchQuery} />
                <RefreshButton refreshFunction={fetchCampaignList} />
            </div>
            {Loading ? (
                <div className="center-loading-icon">
                    <CircularProgress />
                </div>
            ) : errorInFetch ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        marginTop: "40px",
                    }}
                >
                    <span>Hubo un error al cargar las campañas, intenta más tarde.</span>
                    <RefreshButton refreshFunction={fetchCampaignList} />
                </div>
            ) : (
                <React.Fragment>
                    {filteredCampaigns.length > 0 ? (
                    <div style={{ maxHeight: "calc(100vh - 280px)", overflow: "auto" }}>
                        <Table className="regular">
                            <thead>
                                <tr>
                                    {campaignColumns.map((col, index) => (
                                        <th key={index}>{col.label}</th>
                                    ))}
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCampaigns.map((campaign, index) => (
                                        <tr key={index}>
                                            <td>
                                                <MuiButton
                                                    color="primary"
                                                    size="small"
                                                    style={{ textTransform: "none", fontWeight: "500" }}
                                                    onClick={() => openCampaignDetail(campaign)}
                                                    endIcon={<EditarIcono width="15px" height="15px" />}
                                                >
                                                    {campaign.name || ""}
                                                </MuiButton>
                                            </td>
                                            {campaignColumns.slice(1).map((col, j) => (
                                                <td key={j}>
                                                    {col.formatDate
                                                        ? (campaign[col.key] || "").split(",")[0]
                                                        : col.catalog
                                                        ? (catalogTranslations[col.catalog]?.[campaign[col.key]] || campaign[col.key] || "")
                                                        : campaign[col.key] || ""}
                                                </td>
                                            ))}
                                            <td>
                                                <span style={{ color: getStateColor(campaign.state), fontWeight: "500" }}>
                                                    {getStateLabel(campaign.state)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </Table>
                    </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", marginTop: "80px", flex: 1 }}>
                            <p style={{ color: "#27315D", fontSize: "0.9rem" }}>
                                {campaignList.length === 0 ? "No hay campañas disponibles." : "No se encontraron campañas con los filtros seleccionados."}
                            </p>
                            <RefreshButton refreshFunction={fetchCampaignList} />
                        </div>
                    )}
                    <div style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        position: "sticky",
                        bottom: 0,
                        zIndex: 999,
                        backgroundColor: "#fafafa",
                        width: "100%",
                        padding: "16px",
                        alignItems: "center",
                        borderTop: "none",
                    }}>
                        <MuiButton
                            color="primary"
                            size="small"
                            startIcon={<AgregarIcono width="15px" height="15px" />}
                            onClick={openNewCampaignForm}
                            style={{ textTransform: "none" }}
                        >
                            Crear Campaña
                        </MuiButton>
                    </div>
                </React.Fragment>
            )}
        </React.Fragment>
    );
    }

    // ==================== RENDER NEW CAMPAIGN FORM ====================
    const hasNewCampaignChanges = () => {
        return campaignName.trim() !== "" || selectedTemplate !== "" || deliveryType !== "" || sendingType !== "";
    };

    const confirmExitNewCampaign = (navigateFn) => {
        if (hasNewCampaignChanges()) {
            setConfirmDialog({ open: true, onConfirm: navigateFn });
        } else {
            navigateFn();
        }
    };

    const renderNewCampaignForm = () => (
        <React.Fragment>
            <Grid container justifyContent="space-around" style={{ marginTop: "20px" }}>
                <Grid item lg={7} md={9} sm={12} xs={12}>
                    {/* Campo: Nombre de la campaña */}
                    <Grid container spacing={3} alignItems="flex-start" justifyContent="center">
                        <Grid item sm={12} xs={12}>
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid item sm={3} xs={12}>
                                    <label style={{ textAlign: "right", display: "block" }}>
                                        <span style={{ color: "red" }}>* </span>
                                        Nombre de la campaña
                                    </label>
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <TextField
                                        name="campaignName"
                                        value={campaignName}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        placeholder="Escribe el Nombre"
                                        error={errors.campaignName || nameAvailable === false}
                                        helperText={
                                            errors.campaignName ? "* El campo no puede estar vacío."
                                            : checkingName ? "Verificando disponibilidad..."
                                            : nameAvailable === true ? "✓ Nombre disponible"
                                            : nameAvailable === false ? "✗ Este nombre ya está en uso"
                                            : ""
                                        }
                                        FormHelperTextProps={{
                                            style: {
                                                color: nameAvailable === true ? "#44AB3F"
                                                    : nameAvailable === false ? "#f44336"
                                                    : checkingName ? "#B2B2B2"
                                                    : undefined,
                                            },
                                        }}
                                        onChange={(e) => handleCampaignNameChange(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Campo: Plantilla (Autocomplete + Ver Plantillas) */}
                    <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                        <Grid item sm={12} xs={12}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item sm={3} xs={12}>
                                    <label style={{ textAlign: "right", display: "block" }}>
                                        <span style={{ color: "red" }}>* </span>
                                        Plantilla
                                    </label>
                                </Grid>
                                <Grid item sm={5} xs={12}>
                                    <Autocomplete
                                        freeSolo
                                        options={templateList}
                                        inputValue={selectedTemplate}
                                        filterOptions={(options, { inputValue }) => {
                                            if (!inputValue || inputValue.length < 2) return [];
                                            const lower = inputValue.toLowerCase();
                                            return options.filter((opt) => opt.toLowerCase().includes(lower));
                                        }}
                                        onInputChange={(event, newValue, reason) => {
                                            if (reason === "input" || reason === "clear") {
                                                handleTemplateChange(newValue || "");
                                            }
                                        }}
                                        onChange={(event, newValue) => {
                                            if (newValue) handleTemplateChange(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                size="small"
                                                placeholder="Escribe o selecciona una plantilla"
                                                error={errors.selectedTemplate && !selectedTemplate}
                                                helperText={errors.selectedTemplate && !selectedTemplate ? "* Debes seleccionar una plantilla." : ""}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item sm={2} xs={12}>
                                    <CustomButton
                                        onClick={() => openSelectTemplate()}
                                        variant="contained"
                                    >
                                        Ver Plantillas
                                    </CustomButton>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Personalización de parámetros de plantilla */}
                    {templateParams.length > 0 && (
                        <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                            <Grid item sm={12} xs={12}>
                                <Grid container spacing={2} alignItems="flex-start">
                                    <Grid item sm={3} xs={12}>
                                        <label style={{ textAlign: "right", display: "block", fontWeight: "500" }}>
                                            <span style={{ color: "red" }}>* </span>
                                            Parámetros
                                        </label>
                                    </Grid>
                                    <Grid item sm={4} xs={12}>
                                        {templateParams.map((tp, idx) => (
                                            <div key={idx} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                                                <span style={{ minWidth: "100px", fontSize: "0.85rem" }}>Parámetro {idx + 1}</span>
                                                <TextField
                                                    select
                                                    value={tp.source}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    error={tp.source === "" && errors.selectedTemplate}
                                                    helperText={tp.source === "" && errors.selectedTemplate ? `Selecciona un campo para el parámetro {{${idx + 1}}}.` : ""}
                                                    onChange={(e) => {
                                                        const newParams = [...templateParams];
                                                        newParams[idx] = { ...newParams[idx], source: e.target.value };
                                                        setState((prev) => ({ ...prev, templateParams: newParams }));
                                                    }}
                                                >
                                                    <MenuItem value="">Seleccionar campo</MenuItem>
                                                    <MenuItem value="name">Nombre</MenuItem>
                                                    <MenuItem value="age">Edad</MenuItem>
                                                    <MenuItem value="gender">Género</MenuItem>
                                                    <MenuItem value="state">Estado</MenuItem>
                                                    <MenuItem value="locality">Localidad</MenuItem>
                                                    <MenuItem value="sentiment">Nivel de satisfacción</MenuItem>
                                                    <MenuItem value="interactionsCounter">Interacciones</MenuItem>
                                                    <MenuItem value="update_date">Fecha de última modificación</MenuItem>
                                                </TextField>
                                            </div>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {/* Sección 1: Enviar a */}
                    <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                        <Grid item sm={12} xs={12}>
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid item sm={3} xs={12}>
                                    <label style={{ textAlign: "right", display: "block" }}>
                                        <span style={{ color: "red" }}>* </span>
                                        Enviar a
                                        <Tooltip title="Define los destinatarios a los que se enviará la campaña.">
                                            <HelpOutlineIcon color="primary" fontSize="small" style={{ verticalAlign: "middle", marginLeft: "4px", fontSize: "1rem" }} />
                                        </Tooltip>
                                    </label>
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <TextField
                                        select
                                        name="deliveryType"
                                        value={deliveryType}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        onChange={(e) => handleDeliveryTypeChange(e.target.value)}
                                    >
                                        {(catalogTranslations.Campaign_Delivery && Object.keys(catalogTranslations.Campaign_Delivery).length > 0)
                                            ? [
                                                <MenuItem key="" value="" disabled>Seleccionar contactos</MenuItem>,
                                                ...Object.entries(catalogTranslations.Campaign_Delivery)
                                                    .filter(([value]) => value !== "CSV")
                                                    .map(([value, label], i) => (
                                                        <MenuItem key={i} value={value}>{label}</MenuItem>
                                                    ))
                                              ]
                                            : [
                                                <MenuItem key="" value="" disabled>Seleccionar contactos</MenuItem>,
                                                <MenuItem key="ALL" value="ALL">Todos los contactos</MenuItem>,
                                                <MenuItem key="SEGMENTED" value="SEGMENTED">Segmentación</MenuItem>,
                                            ]
                                        }
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Sección 2: Tipo de envío */}
                    <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                        <Grid item sm={12} xs={12}>
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid item sm={3} xs={12}>
                                    <label style={{ textAlign: "right", display: "block" }}>
                                        <span style={{ color: "red" }}>* </span>
                                        Tipo de envío
                                        <Tooltip title="Define el momento en que se envía la campaña.">
                                            <HelpOutlineIcon color="primary" fontSize="small" style={{ verticalAlign: "middle", marginLeft: "4px", fontSize: "1rem" }} />
                                        </Tooltip>
                                    </label>
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <TextField
                                        select
                                        name="sendingType"
                                        value={sendingType}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        onChange={(e) => handleSendingTypeChange(e.target.value)}
                                    >
                                        {(catalogTranslations.Campaign_Sending && Object.keys(catalogTranslations.Campaign_Sending).length > 0)
                                            ? [
                                                <MenuItem key="" value="" disabled>Seleccionar envío</MenuItem>,
                                                ...Object.entries(catalogTranslations.Campaign_Sending).map(([value, label], i) => (
                                                    <MenuItem key={i} value={value}>{label}</MenuItem>
                                                ))
                                              ]
                                            : [
                                                <MenuItem key="" value="" disabled>Seleccionar envío</MenuItem>,
                                                <MenuItem key="INMEDIATE" value="INMEDIATE">Envío Inmediato</MenuItem>,
                                                <MenuItem key="SCHEDULED" value="SCHEDULED">Envío Programado</MenuItem>,
                                            ]
                                        }
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Preview de Segmentación (si delivery es SEGMENTED y hay filtros) */}
                    {deliveryType === "SEGMENTED" && (segFilters.age.operator || segFilters.gender.length > 0 || segFilters.state.length > 0 || segFilters.locality.length > 0 || segFilters.interests.length > 0 || segFilters.intention.length > 0 || segFilters.sentiment || segFilters.interactions.operator || segFilters.lastModifiedDate.type) && (
                        <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                            <Grid item sm={12} xs={12}>
                                <Grid container spacing={2} alignItems="flex-start">
                                    <Grid item sm={3} xs={12}>
                                        <label style={{ textAlign: "right", display: "block", fontWeight: "500" }}>
                                            Segmentación
                                            <IconButton size="small" onClick={() => { fetchCampaignCatalogs(); setState((prev) => ({ ...prev, section: "segmentation" })); }} style={{ marginLeft: "5px" }}>
                                                <EditarIcono width="15px" height="15px" />
                                            </IconButton>
                                        </label>
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <div style={{ backgroundColor: "#f5f5f5", borderRadius: "8px", padding: "10px 15px", fontSize: "0.85rem" }}>
                                            {segFilters.age.operator && segFilters.age.ageFrom && (
                                                <p style={{ margin: "3px 0" }}>
                                                    <strong>Edad:</strong> {segFilters.age.operator === "BETWEEN" ? `${segFilters.age.ageFrom} - ${segFilters.age.ageTo}` : `${segFilters.age.operator === "EQUALS" ? "Igual a" : segFilters.age.operator === "GT" ? "Mayor a" : "Menor a"} ${segFilters.age.ageFrom}`}
                                                </p>
                                            )}
                                            {segFilters.gender.length > 0 && <p style={{ margin: "3px 0" }}><strong>Género:</strong> {segFilters.gender.map((g) => segCatalogOptions.gender.find((o) => o.value === g)?.label || g).join(", ")}</p>}
                                            {segFilters.state.length > 0 && <p style={{ margin: "3px 0" }}><strong>Estado:</strong> {segFilters.state.join(", ")}</p>}
                                            {segFilters.locality.length > 0 && <p style={{ margin: "3px 0" }}><strong>Localidad:</strong> {segFilters.locality.join(", ")}</p>}
                                            {segFilters.interests.length > 0 && <p style={{ margin: "3px 0" }}><strong>Intereses:</strong> {segFilters.interests.join(", ")}</p>}
                                            {segFilters.intention.length > 0 && <p style={{ margin: "3px 0" }}><strong>Intención:</strong> {segFilters.intention.join(", ")}</p>}
                                            {segFilters.sentiment && <p style={{ margin: "3px 0" }}><strong>Nivel de Satisfacción:</strong> {segCatalogOptions.sentiment.find((o) => o.value === segFilters.sentiment)?.label || segFilters.sentiment}</p>}
                                            {segFilters.interactions.operator && segFilters.interactions.from && (
                                                <p style={{ margin: "3px 0" }}>
                                                    <strong>Interacciones:</strong> {segFilters.interactions.operator === "BETWEEN" ? `${segFilters.interactions.from} - ${segFilters.interactions.to}` : `${segFilters.interactions.operator === "EQUALS" ? "Igual a" : segFilters.interactions.operator === "GT" ? "Mayor a" : "Menor a"} ${segFilters.interactions.from}`}
                                                </p>
                                            )}
                                            {segFilters.lastModifiedDate.type && (
                                                <p style={{ margin: "3px 0" }}>
                                                    <strong>Fecha últ. interacción:</strong> {
                                                        segFilters.lastModifiedDate.type === "CUSTOM"
                                                            ? `${segFilters.lastModifiedDate.from} a ${segFilters.lastModifiedDate.to}`
                                                            : segFilters.lastModifiedDate.type === "LAST_7_DAYS" ? "Últimos 7 días"
                                                            : segFilters.lastModifiedDate.type === "LAST_30_DAYS" ? "Últimos 30 días"
                                                            : segFilters.lastModifiedDate.type === "THIS_MONTH" ? "Este mes"
                                                            : segFilters.lastModifiedDate.type === "PREVIOUS_MONTH" ? "Mes anterior"
                                                            : segFilters.lastModifiedDate.type
                                                    }
                                                </p>
                                            )}
                                        </div>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {/* Preview de Programación (si sending es SCHEDULED y hay datos) */}
                    {sendingType === "SCHEDULED" && startDate && startTime && (
                        <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                            <Grid item sm={12} xs={12}>
                                <Grid container spacing={2} alignItems="flex-start">
                                    <Grid item sm={3} xs={12}>
                                        <label style={{ textAlign: "right", display: "block", fontWeight: "500" }}>
                                            Programación
                                            <IconButton size="small" onClick={() => { fetchCampaignCatalogs(); setState((prev) => ({ ...prev, section: "scheduling" })); }} style={{ marginLeft: "5px" }}>
                                                <EditarIcono width="15px" height="15px" />
                                            </IconButton>
                                        </label>
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <div style={{ backgroundColor: "#f5f5f5", borderRadius: "8px", padding: "10px 15px", fontSize: "0.85rem" }}>
                                            <p style={{ margin: "3px 0" }}><strong>Fecha inicio:</strong> {startDate}</p>
                                            <p style={{ margin: "3px 0" }}><strong>Hora inicio:</strong> {startTime}</p>
                                            {recurrent ? (
                                                <React.Fragment>
                                                    <p style={{ margin: "3px 0" }}><strong>Tipo:</strong> Recurrente</p>
                                                    <p style={{ margin: "3px 0" }}><strong>Frecuencia:</strong> {frequency === "WEEKLY" ? "Semanal" : "Diaria"}</p>
                                                    {endDate && <p style={{ margin: "3px 0" }}><strong>Fecha fin:</strong> {endDate}</p>}
                                                    {frequency === "WEEKLY" && daysOfWeek.length > 0 && (
                                                        <p style={{ margin: "3px 0" }}><strong>Días:</strong> {daysOfWeek.map(d => {
                                                            const dayObj = weekDays.find(wd => wd.value === d);
                                                            return dayObj ? dayObj.label : d;
                                                        }).join(", ")}</p>
                                                    )}
                                                </React.Fragment>
                                            ) : (
                                                <p style={{ margin: "3px 0" }}><strong>Tipo:</strong> Una sola ejecución</p>
                                            )}
                                        </div>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    <FooterControls
                        disableCancel={!hasNewCampaignChanges()}
                        disableSave={!campaignName.trim() || !selectedTemplate || nameAvailable === false || !deliveryType || !sendingType || templateParams.some((tp) => tp.source === "")}
                        onCancel={() => backToList()}
                        saveTitle="¿Estás seguro de que quieres crear la campaña?"
                        saveMessage="Esta acción guardará las modificaciones realizadas."
                        onSave={async () => {
                            const remeberAuth = localStorage.getItem("remeberAuthCredentials");
                            const email = remeberAuth
                                ? localStorage.getItem("email")
                                : sessionStorage.getItem("email");

                            let campaignData = {
                                campaignName: campaignName.trim(),
                                templateName: selectedTemplate,
                                delivery: deliveryType,
                                sending: sendingType,
                                email: email,
                            };

                            // Agregar templateParams si hay parámetros detectados
                            if (templateParams.length > 0) {
                                campaignData.templateParams = templateParams;
                            }

                            if (sendingType === "SCHEDULED" && startDate && startTime) {
                                if (recurrent) {
                                    campaignData.schedule = {
                                        type: "RECURRING",
                                        frequency: frequency,
                                        startDate: `${startDate}T${startTime}:00`,
                                        endDate: endDate ? `${endDate}T23:59:59` : undefined,
                                    };
                                    if (frequency === "WEEKLY" && daysOfWeek.length > 0) {
                                        campaignData.schedule.daysOfWeek = daysOfWeek;
                                    }
                                    // Envío único: enviar una sola vez a cada contacto aunque sea recurrente
                                    if (sendOnce) {
                                        campaignData.sendOnce = true;
                                    }
                                } else {
                                    campaignData.schedule = {
                                        type: "ONE_TIME",
                                        executionDateTime: `${startDate}T${startTime}:00`,
                                    };
                                }
                            }

                            if (deliveryType === "SEGMENTED") {
                                // IMPORTANTE: usar EXACTAMENTE el mismo formato que fetchSegmentPreview
                                // (campos en minúsculas, interacciones y fecha como campos separados)
                                // para que el filtrado del backend al enviar coincida con la vista previa.
                                let segments = [];
                                if (segFilters.age.operator && segFilters.age.ageFrom) {
                                    if (segFilters.age.operator === "BETWEEN") {
                                        segments.push({ field: "age", operator: "BETWEEN", value: { min: parseInt(segFilters.age.ageFrom), max: parseInt(segFilters.age.ageTo) } });
                                    } else {
                                        segments.push({ field: "age", operator: segFilters.age.operator, value: parseInt(segFilters.age.ageFrom) });
                                    }
                                }
                                if (segFilters.gender.length > 0) {
                                    let seg = { field: "gender", value: segFilters.gender.length === 1 ? segFilters.gender[0] : segFilters.gender.join(", ") };
                                    if (segOperators.gender) seg.operator = "NOT_IN";
                                    segments.push(seg);
                                }
                                if (segFilters.state.length > 0) {
                                    let seg = { field: "state", value: segFilters.state.length === 1 ? segFilters.state[0] : segFilters.state.join(", ") };
                                    if (segOperators.state) seg.operator = "NOT_IN";
                                    segments.push(seg);
                                }
                                if (segFilters.locality.length > 0) {
                                    let seg = { field: "locality", value: segFilters.locality.length === 1 ? segFilters.locality[0] : segFilters.locality.join(", ") };
                                    if (segOperators.locality) seg.operator = "NOT_IN";
                                    segments.push(seg);
                                }
                                if (segFilters.interests.length > 0) {
                                    let seg = { field: "interests", value: segFilters.interests.length === 1 ? segFilters.interests[0] : segFilters.interests.join(", ") };
                                    seg.operator = segOperators.interests ? "NOT_IN" : "CONTAINS";
                                    segments.push(seg);
                                }
                                if (segFilters.intention.length > 0) {
                                    let seg = { field: "intent", value: segFilters.intention.length === 1 ? segFilters.intention[0] : segFilters.intention.join(", ") };
                                    seg.operator = segOperators.intention ? "NOT_IN" : "CONTAINS";
                                    segments.push(seg);
                                }
                                if (segFilters.sentiment) {
                                    const sentimentValue = segFilters.sentiment.charAt(0).toUpperCase() + segFilters.sentiment.slice(1).toLowerCase();
                                    segments.push({ field: "sentiment", operator: "EQUALS", value: sentimentValue });
                                }
                                // Filtro de interacciones (dentro del array segment)
                                if (segFilters.interactions.operator && segFilters.interactions.from) {
                                    const op = segFilters.interactions.operator;
                                    const from = parseInt(segFilters.interactions.from);
                                    if (op === "BETWEEN" && segFilters.interactions.to) {
                                        segments.push({ field: "interactionsCounter", operator: op, value: { min: from, max: parseInt(segFilters.interactions.to) } });
                                    } else {
                                        segments.push({ field: "interactionsCounter", operator: op, value: from });
                                    }
                                }
                                // Filtro de fecha de última interacción (dentro del array segment)
                                if (segFilters.lastModifiedDate && segFilters.lastModifiedDate.type) {
                                    if (segFilters.lastModifiedDate.type === "CUSTOM") {
                                        if (segFilters.lastModifiedDate.from && segFilters.lastModifiedDate.to) {
                                            segments.push({ field: "update_date", operator: "CUSTOM", value: { from: segFilters.lastModifiedDate.from, to: segFilters.lastModifiedDate.to } });
                                        }
                                    } else {
                                        segments.push({ field: "update_date", operator: segFilters.lastModifiedDate.type, value: segFilters.lastModifiedDate.type });
                                    }
                                }
                                if (segments.length > 0) {
                                    campaignData.segment = segments;
                                }
                            }

                            console.log("Create campaign payload:", campaignData);
                            updateContextAttribute("loadingDialog", true);
                            updateContextAttribute("LoadingMessage", "Creando campaña...");

                            let res = await createCampaign(campaignData);
                            updateContextAttribute("loadingDialog", false);
                            updateContextAttribute("LoadingMessage", "");

                            let responseCode = res?.data?.response?.code ? parseInt(res.data.response.code) : res?.status === 200 || res?.status === 201 ? 0 : 99;
                            if (responseCode === 0 || res?.status === 200 || res?.status === 201) {
                                updateContextAttribute("alert", {
                                    open: true,
                                    severity: "success",
                                    message: "Campaña creada correctamente",
                                });
                                fetchCampaignList();
                                backToList();
                            } else {
                                Swal.fire({
                                    icon: "error",
                                    title: "Error al crear la campaña",
                                    text: res?.data?.response?.message || "Intenta más tarde.",
                                });
                            }
                        }}
                        saveButtonName="Guardar"
                    />
                </Grid>
            </Grid>
        </React.Fragment>
    );

    // ==================== RENDER SEGMENTATION VIEW ====================
    const hasSegmentationFilters = () => {
        return segFilters.age.operator !== "" ||
            segFilters.gender.length > 0 ||
            segFilters.state.length > 0 ||
            segFilters.locality.length > 0 ||
            segFilters.interests.length > 0 ||
            segFilters.intention.length > 0 ||
            segFilters.sentiment !== "" ||
            segFilters.interactions.operator !== "" ||
            segFilters.lastModifiedDate.type !== "";
    };
    const segPreviewTimer = useRef(null);

    const handleSegFilterChange = (filterName, value) => {
        // Solo actualiza el state — NO hace fetch. El usuario usa "Calcular segmentación"
        setState((prev) => ({
            ...prev,
            segFilters: {
                ...prev.segFilters,
                [filterName]: value,
            },
        }));
    };

    const handleSegFilterApply = (filterName, values) => {
        // Solo actualiza el state — NO hace fetch
        setState((prev) => ({
            ...prev,
            segFilters: { ...prev.segFilters, [filterName]: values },
        }));
    };

    const handleCalculateSegmentation = () => {
        // Ejecuta una sola petición con todos los filtros actuales
        updateContextAttribute("loadingDialog", true);
        updateContextAttribute("LoadingMessage", "Consultando segmentación...");
        setState((prev) => ({ ...prev, segPage: 1 }));
        fetchSegmentPreview(segFilters, 1);
    };

    const handleSegExcludeChange = (filterName, checked) => {
        setState((prev) => ({
            ...prev,
            segOperators: { ...prev.segOperators, [filterName]: checked },
        }));
    };

    const handleSegPageChange = (newPage) => {
        setState((prev) => ({ ...prev, segPage: newPage }));
        fetchSegmentPreview(segFilters, newPage, true);
    };

    const fetchSegmentPreview = async (filters, page, isPageChange) => {
        // Build segments array from filters
        let segments = [];
        if (filters.age.operator && filters.age.ageFrom) {
            if (filters.age.operator === "BETWEEN") {
                segments.push({ field: "age", operator: "BETWEEN", value: { min: parseInt(filters.age.ageFrom), max: parseInt(filters.age.ageTo) } });
            } else {
                segments.push({ field: "age", operator: filters.age.operator, value: parseInt(filters.age.ageFrom) });
            }
        }
        if (filters.gender && filters.gender.length > 0) {
            let seg = { field: "gender", value: filters.gender.length === 1 ? filters.gender[0] : filters.gender.join(", ") };
            if (segOperators.gender) seg.operator = "NOT_IN";
            segments.push(seg);
        }
        if (filters.state && filters.state.length > 0) {
            let seg = { field: "state", value: filters.state.length === 1 ? filters.state[0] : filters.state.join(", ") };
            if (segOperators.state) seg.operator = "NOT_IN";
            segments.push(seg);
        }
        if (filters.locality.length > 0) {
            let seg = { field: "locality", value: filters.locality.length === 1 ? filters.locality[0] : filters.locality.join(", ") };
            if (segOperators.locality) seg.operator = "NOT_IN";
            segments.push(seg);
        }
        if (filters.interests.length > 0) {
            let seg = { field: "interests", value: filters.interests.length === 1 ? filters.interests[0] : filters.interests.join(", ") };
            seg.operator = segOperators.interests ? "NOT_IN" : "CONTAINS";
            segments.push(seg);
        }
        if (filters.intention.length > 0) {
            let seg = { field: "intent", value: filters.intention.length === 1 ? filters.intention[0] : filters.intention.join(", ") };
            seg.operator = segOperators.intention ? "NOT_IN" : "CONTAINS";
            segments.push(seg);
        }
        if (filters.sentiment) {
            const sentimentValue = filters.sentiment.charAt(0).toUpperCase() + filters.sentiment.slice(1).toLowerCase();
            segments.push({ field: "sentiment", operator: "EQUALS", value: sentimentValue });
        }
        // Filtro de interacciones (dentro del array segment)
        if (filters.interactions && filters.interactions.operator && filters.interactions.from) {
            const op = filters.interactions.operator;
            const from = parseInt(filters.interactions.from);
            if (op === "BETWEEN" && filters.interactions.to) {
                segments.push({ field: "interactionsCounter", operator: op, value: { min: from, max: parseInt(filters.interactions.to) } });
            } else {
                segments.push({ field: "interactionsCounter", operator: op, value: from });
            }
        }
        // Filtro de fecha de última interacción (dentro del array segment)
        if (filters.lastModifiedDate && filters.lastModifiedDate.type) {
            if (filters.lastModifiedDate.type === "CUSTOM") {
                if (filters.lastModifiedDate.from && filters.lastModifiedDate.to) {
                    segments.push({ field: "update_date", operator: "CUSTOM", value: { from: filters.lastModifiedDate.from, to: filters.lastModifiedDate.to } });
                }
            } else {
                segments.push({ field: "update_date", operator: filters.lastModifiedDate.type, value: filters.lastModifiedDate.type });
            }
        }

        // Ya no se usan como campos separados; van dentro de segments
        let interactionsFilter = null;
        let lastModifiedFilter = null;

        process.env.REACT_APP_DEBUG === "true" && console.log("fetchSegmentPreview segments:", JSON.stringify(segments));

        setState((prev) => ({ ...prev, loadingPreview: !isPageChange, loadingTable: true }));
        try {
            let res = await previewSegment(segments, page || segPage, segPageSize, interactionsFilter, lastModifiedFilter);
            // Support multiple response structures (including error responses from Axios)
            const responseData = res?.data?.response || res?.data?.body?.response || res?.response?.data?.response || res?.response?.data || res?.data;
            process.env.REACT_APP_DEBUG === "true" && console.log("fetchSegmentPreview responseData:", responseData);
            if (responseData && responseData.totalContacts != null) {
                const filtersApplied = responseData.filtersApplied !== false && segments.length > 0;
                setState((prev) => ({
                    ...prev,
                    totalContacts: responseData.totalContacts,
                    segmentPreview: filtersApplied ? {
                        total: responseData.totalContacts,
                        matched: responseData.matchedContacts,
                        percentage: responseData.percentage,
                    } : null,
                    segContacts: responseData.contacts || [],
                    segTotalPages: responseData.pagination?.totalPages || Math.ceil((responseData.totalContacts || 0) / segPageSize),
                    segPage: page || prev.segPage,
                    loadingPreview: false,
                    loadingTable: false,
                }));
                updateContextAttribute("loadingDialog", false);
                updateContextAttribute("LoadingMessage", "");
            } else {
                setState((prev) => ({ ...prev, loadingPreview: false, loadingTable: false }));
                updateContextAttribute("loadingDialog", false);
                updateContextAttribute("LoadingMessage", "");
            }
        } catch (error) {
            setState((prev) => ({ ...prev, loadingPreview: false, loadingTable: false }));
            updateContextAttribute("loadingDialog", false);
            updateContextAttribute("LoadingMessage", "");
        }
    };

    const renderSegmentation = () => (
        <React.Fragment>
            <div style={{ marginBottom: "100px" }}>
                <div style={{ fontSize: "0.8rem", color: "#27315D" }}>
                    {(() => {
                        const remeberAuth = localStorage.getItem("remeberAuthCredentials");
                        const dayCounter  = parseInt(remeberAuth ? localStorage.getItem("outgoingMessageDayCounter")  : sessionStorage.getItem("outgoingMessageDayCounter"))  || 0;
                        const dayLimit    = parseInt(remeberAuth ? localStorage.getItem("outgoingMessageDayLimit")    : sessionStorage.getItem("outgoingMessageDayLimit"))    || 0;
                        const totalCounter= parseInt(remeberAuth ? localStorage.getItem("outgoingMessageCounter")     : sessionStorage.getItem("outgoingMessageCounter"))     || 0;
                        const totalLimit  = parseInt(remeberAuth ? localStorage.getItem("outgoingMessageLimit")       : sessionStorage.getItem("outgoingMessageLimit"))       || 0;
                        return (
                            <React.Fragment>
                                <p style={{ margin: "1px 0" }}>Mensajes disponibles de hoy {dayCounter} de {dayLimit}</p>
                                <p style={{ margin: "1px 0" }}>Mensajes disponibles totales {totalCounter} de {totalLimit}</p>
                            </React.Fragment>
                        );
                    })()}
                </div>
            </div>
            <div style={{ position: "relative" }}>
                {/* Gráfica — posición absoluta arriba derecha */}
                <div style={{ position: "absolute", top: "-150px", right: "80px", zIndex: 1 }}>
                    <div style={{ textAlign: "center", width: "120px", height: "140px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        {loadingPreview ? (
                            <div>
                                <CircularProgress size={24} />
                            </div>
                        ) : (
                            <div>
                                <svg width="90" height="90" viewBox="0 0 36 36">
                                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#27315D" strokeWidth="3" />
                                    {segmentPreview && (
                                        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#70C5D8" strokeWidth="3"
                                            strokeDasharray={`${segmentPreview.percentage} ${100 - segmentPreview.percentage}`}
                                            strokeDashoffset="25" strokeLinecap="round" />
                                    )}
                                    <text x="18" y="18" textAnchor="middle" dy=".3em" style={{ fontSize: "0.35rem", fill: "#27315d", fontWeight: "bold" }}>
                                        {segmentPreview ? `${segmentPreview.percentage}%` : "100%"}
                                    </text>
                                </svg>
                                <div style={{ fontSize: "0.75rem", textAlign: "center" }}>
                                    <p style={{ margin: "2px 0" }}><strong>Total:</strong> {totalContacts > 0 ? totalContacts.toLocaleString() : "—"}</p>
                                    {segmentPreview && (
                                        <p style={{ margin: "2px 0" }}><strong>Segmento:</strong> {segmentPreview.matched?.toLocaleString()} ({segmentPreview.percentage}%)</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Filtros + Tabla (ancho completo) */}
                <div>
                    {/* Filtros */}
                    <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", flexWrap: "wrap", overflow: "visible" }}>
                        {/* Edad */}
                        <div style={{ width: "calc(11% - 8px)", minWidth: "80px", position: "relative" }}>
                            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", fontSize: "0.75rem" }}>
                                Edad
                            </label>
                            <AgeFilter
                                value={segFilters.age}
                                onChange={(val) => handleSegFilterChange("age", val)}
                            />
                        </div>

                        {/* Género */}
                        <div style={{ width: "calc(11% - 8px)", minWidth: "80px" }}>
                            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", fontSize: "0.75rem" }}>
                                Género
                            </label>
                            <MultiSelectFilter
                                value={segFilters.gender}
                                onChange={(val) => handleSegFilterChange("gender", val)}
                                options={segCatalogOptions.gender}
                                placeholder="Seleccionar..."
                                loading={loadingSegOptions}
                                exclude={segOperators.gender}
                                onExcludeChange={(checked) => handleSegExcludeChange("gender", checked)}
                            />
                        </div>

                        {/* Estado */}
                        <div style={{ width: "calc(11% - 8px)", minWidth: "80px" }}>
                            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", fontSize: "0.75rem" }}>
                                Estado
                            </label>
                            <MultiSelectFilter
                                value={segFilters.state}
                                onChange={(val) => handleSegFilterChange("state", val)}
                                options={segDynamicOptions.state}
                                placeholder="Seleccionar..."
                                loading={loadingSegOptions}
                                exclude={segOperators.state}
                                onExcludeChange={(checked) => handleSegExcludeChange("state", checked)}
                            />
                        </div>

                        {/* Localidad */}
                        <div style={{ width: "calc(11% - 8px)", minWidth: "80px" }}>
                            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", fontSize: "0.75rem" }}>
                                Localidad
                            </label>
                            <MultiSelectFilter
                                value={segFilters.locality}
                                onChange={(val) => handleSegFilterChange("locality", val)}
                                options={segDynamicOptions.locality}
                                placeholder="Seleccionar..."
                                loading={loadingSegOptions}
                                exclude={segOperators.locality}
                                onExcludeChange={(checked) => handleSegExcludeChange("locality", checked)}
                            />
                        </div>

                        {/* Intereses */}
                        <div style={{ width: "calc(11% - 8px)", minWidth: "80px" }}>
                            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", fontSize: "0.75rem" }}>
                                Intereses
                            </label>
                            <MultiSelectFilter
                                value={segFilters.interests}
                                onChange={(val) => handleSegFilterChange("interests", val)}
                                options={segDynamicOptions.interests}
                                placeholder="Seleccionar..."
                                loading={loadingSegOptions}
                                exclude={segOperators.interests}
                                onExcludeChange={(checked) => handleSegExcludeChange("interests", checked)}
                            />
                        </div>

                        {/* Intención */}
                        <div style={{ width: "calc(11% - 8px)", minWidth: "80px" }}>
                            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", fontSize: "0.75rem" }}>
                                Intención
                            </label>
                            <MultiSelectFilter
                                value={segFilters.intention}
                                onChange={(val) => handleSegFilterChange("intention", val)}
                                options={segDynamicOptions.intention}
                                placeholder="Seleccionar..."
                                loading={loadingSegOptions}
                                exclude={segOperators.intention}
                                onExcludeChange={(checked) => handleSegExcludeChange("intention", checked)}
                            />
                        </div>

                        {/* Nivel de Satisfacción */}
                        <div style={{ width: "calc(11% - 8px)", minWidth: "80px" }}>
                            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", fontSize: "0.75rem" }}>
                                Nivel de Satisfacción
                            </label>
                            <SingleSelectFilter
                                value={segFilters.sentiment}
                                onChange={(val) => handleSegFilterChange("sentiment", val)}
                                options={segCatalogOptions.sentiment}
                                placeholder="Seleccionar..."
                                loading={loadingSegOptions}
                            />
                        </div>

                        {/* Interacciones */}
                        <div style={{ width: "calc(11% - 8px)", minWidth: "80px", position: "relative" }}>
                            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", fontSize: "0.75rem" }}>
                                Interacciones
                            </label>
                            <TextField
                                fullWidth
                                select
                                variant="outlined"
                                size="small"
                                value={segFilters.interactions.operator}
                                onChange={(e) => {
                                    const op = e.target.value;
                                    if (op === "") {
                                        handleSegFilterChange("interactions", { operator: "", from: "", to: "" });
                                    } else {
                                        setState((prev) => ({
                                            ...prev,
                                            segFilters: { ...prev.segFilters, interactions: { operator: op, from: "", to: "" } },
                                        }));
                                    }
                                }}
                            >
                                <MenuItem value="">Seleccionar...</MenuItem>
                                <MenuItem value="EQUALS">Igual a</MenuItem>
                                <MenuItem value="GT">Mayor a</MenuItem>
                                <MenuItem value="LT">Menor a</MenuItem>
                                <MenuItem value="BETWEEN">Rango</MenuItem>
                            </TextField>
                            {(segFilters.interactions.operator === "EQUALS" || segFilters.interactions.operator === "GT" || segFilters.interactions.operator === "LT") && (
                                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: "4px", zIndex: 1 }}>
                                    <TextField
                                        type="number"
                                        value={segFilters.interactions.from}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        placeholder="Cantidad"
                                        inputProps={{ min: 0 }}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const newInteractions = { ...segFilters.interactions, from: val };
                                            handleSegFilterChange("interactions", newInteractions);
                                        }}
                                    />
                                </div>
                            )}
                            {segFilters.interactions.operator === "BETWEEN" && (
                                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: "4px", zIndex: 1, display: "flex", gap: "4px", alignItems: "center" }}>
                                    <TextField
                                        type="number"
                                        value={segFilters.interactions.from}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        placeholder="Desde"
                                        inputProps={{ min: 0 }}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setState((prev) => ({
                                                ...prev,
                                                segFilters: { ...prev.segFilters, interactions: { ...prev.segFilters.interactions, from: val } },
                                            }));
                                            if (val && segFilters.interactions.to) {
                                                handleSegFilterChange("interactions", { ...segFilters.interactions, from: val });
                                            }
                                        }}
                                    />
                                    <span>—</span>
                                    <TextField
                                        type="number"
                                        value={segFilters.interactions.to}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        placeholder="Hasta"
                                        inputProps={{ min: 0 }}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const newInteractions = { ...segFilters.interactions, to: val };
                                            if (segFilters.interactions.from && val) {
                                                handleSegFilterChange("interactions", newInteractions);
                                            } else {
                                                setState((prev) => ({
                                                    ...prev,
                                                    segFilters: { ...prev.segFilters, interactions: newInteractions },
                                                }));
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Fecha de última modificación */}
                        <div style={{ width: "calc(11% - 8px)", minWidth: "80px", position: "relative" }}>
                            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", fontSize: "0.75rem" }}>
                                Fecha últ. interacción
                            </label>
                            <TextField
                                fullWidth
                                select
                                variant="outlined"
                                size="small"
                                value={segFilters.lastModifiedDate.type}
                                onChange={(e) => {
                                    const type = e.target.value;
                                    if (type === "CUSTOM") {
                                        setState((prev) => ({
                                            ...prev,
                                            segFilters: { ...prev.segFilters, lastModifiedDate: { type: "CUSTOM", from: "", to: "" } },
                                        }));
                                    } else {
                                        setState((prev) => ({
                                            ...prev,
                                            segFilters: { ...prev.segFilters, lastModifiedDate: { type, from: "", to: "" } },
                                            segPage: 1,
                                        }));
                                    }
                                }}
                            >
                                <MenuItem value="">Seleccionar...</MenuItem>
                                <MenuItem value="LAST_7_DAYS">Últimos 7 días</MenuItem>
                                <MenuItem value="LAST_30_DAYS">Últimos 30 días</MenuItem>
                                <MenuItem value="THIS_MONTH">Este mes</MenuItem>
                                <MenuItem value="PREVIOUS_MONTH">Mes anterior</MenuItem>
                                <MenuItem value="CUSTOM">Personalizado</MenuItem>
                            </TextField>
                            {segFilters.lastModifiedDate.type === "CUSTOM" && (
                                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: "4px", zIndex: 1, display: "flex", gap: "4px" }}>
                                    <TextField
                                        type="date"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={segFilters.lastModifiedDate.from}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ max: segFilters.lastModifiedDate.to || undefined }}
                                        onChange={(e) => {
                                            const from = e.target.value;
                                            setState((prev) => ({
                                                ...prev,
                                                segFilters: { ...prev.segFilters, lastModifiedDate: { ...prev.segFilters.lastModifiedDate, from } },
                                            }));
                                        }}
                                    />
                                    <TextField
                                        type="date"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={segFilters.lastModifiedDate.to}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ min: segFilters.lastModifiedDate.from || undefined }}
                                        onChange={(e) => {
                                            const to = e.target.value;
                                            setState((prev) => ({
                                                ...prev,
                                                segFilters: { ...prev.segFilters, lastModifiedDate: { ...prev.segFilters.lastModifiedDate, to } },
                                                segPage: 1,
                                            }));
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tabla de contactos */}
                    <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 520px)", marginTop: "50px" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.75rem" }}>
                            <thead style={{ position: "sticky", top: 0, zIndex: 3, background: "#fff", boxShadow: "inset 0 -2px 0 #27315D" }}>
                                <tr>
                                    <th style={{ padding: "6px 8px", textAlign: "left", color: "#27315D", position: "sticky", left: 0, background: "#fff", zIndex: 4, minWidth: "70px", boxShadow: "inset 0 -2px 0 #27315D" }}>Nombre</th>
                                    <th style={{ padding: "6px 8px", textAlign: "left", color: "#27315D", position: "sticky", left: "78px", background: "#fff", zIndex: 4, minWidth: "100px", borderRight: "1px solid #e3e3e3", boxShadow: "inset 0 -2px 0 #27315D" }}>Teléfono</th>
                                    <th style={{ padding: "6px 8px", textAlign: "left", color: "#27315D" }}>Edad</th>
                                    <th style={{ padding: "6px 8px", textAlign: "left", color: "#27315D" }}>Género</th>
                                    <th style={{ padding: "6px 8px", textAlign: "left", color: "#27315D" }}>Estado</th>
                                    <th style={{ padding: "6px 8px", textAlign: "left", color: "#27315D" }}>Localidad</th>
                                    <th style={{ padding: "6px 8px", textAlign: "left", color: "#27315D" }}>Intereses</th>
                                    <th style={{ padding: "6px 8px", textAlign: "left", color: "#27315D" }}>Intención</th>
                                    <th style={{ padding: "6px 8px", textAlign: "left", color: "#27315D" }}>Nivel de Satisfacción</th>
                                    <th style={{ padding: "6px 8px", textAlign: "left", color: "#27315D" }}>Interacciones</th>
                                    <th style={{ padding: "6px 8px", textAlign: "left", color: "#27315D" }}>Fecha de última interacción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {segContacts.length > 0 ? (
                                    segContacts.map((contact, idx) => (
                                        <tr key={idx} style={{ borderBottom: "1px solid #e3e3e3" }}>
                                            <td style={{ padding: "6px 8px", position: "sticky", left: 0, background: "#fff", zIndex: 1, minWidth: "70px" }}>{contact.name || ""}</td>
                                            <td style={{ padding: "6px 8px", position: "sticky", left: "78px", background: "#fff", zIndex: 1, minWidth: "100px", borderRight: "1px solid #e3e3e3" }}>{contact.phone_number || ""}</td>
                                            <td style={{ padding: "6px 8px" }}>{contact.age || ""}</td>
                                            <td style={{ padding: "6px 8px" }}>{segCatalogOptions.gender.find((o) => o.value?.toLowerCase() === contact.gender?.toLowerCase())?.label || contact.gender || ""}</td>
                                            <td style={{ padding: "6px 8px" }}>{contact.estate || contact.state || ""}</td>
                                            <td style={{ padding: "6px 8px" }}>{contact.locality || ""}</td>
                                            <td style={{ padding: "6px 8px", maxWidth: "220px", verticalAlign: "top" }}>{renderCellList(contact.interests)}</td>
                                            <td style={{ padding: "6px 8px", maxWidth: "220px", verticalAlign: "top" }}>{renderCellList(contact.intention || contact.intent)}</td>
                                            <td style={{ padding: "6px 8px" }}>{segCatalogOptions.sentiment.find((o) => o.value?.toLowerCase() === contact.sentiment?.toLowerCase())?.label || contact.sentiment || ""}</td>
                                            <td style={{ padding: "6px 8px" }}>{contact.interactionsCounter != null ? contact.interactionsCounter : ""}</td>
                                            <td style={{ padding: "6px 8px" }}>{contact.update_date ? new Date(typeof contact.update_date === "object" ? contact.update_date.$date : contact.update_date).toLocaleDateString("es-MX") : ""}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={11} style={{ padding: "20px", textAlign: "center", color: "#B2B2B2" }}>
                                            {(loadingPreview || loadingTable) ? "Cargando..." : "Sin contactos"}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                </div>
            </div>

            {/* Paginación — fuera del contenedor de columnas */}
            {segTotalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "15px", marginTop: "12px", marginBottom: "80px" }}>
                    <MuiButton
                        size="small"
                        disabled={segPage <= 1}
                        onClick={() => handleSegPageChange(segPage - 1)}
                        style={{ textTransform: "none" }}
                    >
                        Anterior
                    </MuiButton>
                    <span style={{ fontSize: "0.85rem", color: "#27315D" }}>
                        Página {segPage} de {segTotalPages}
                    </span>
                    <MuiButton
                        size="small"
                        disabled={segPage >= segTotalPages}
                        onClick={() => handleSegPageChange(segPage + 1)}
                        style={{ textTransform: "none" }}
                    >
                        Siguiente
                    </MuiButton>
                </div>
            )}

            <FooterControls
                disableCancel={!hasSegmentationFilters()}
                disableSave={!segmentPreview}
                noCancel={false}
                saveTitle="¿Deseas guardar la segmentación?"
                saveMessage="Se aplicarán los filtros configurados a la campaña."
                onCancel={() => {
                    setState((prev) => ({
                        ...prev,
                        segFilters: {
                            age: { operator: "", ageFrom: "", ageTo: "" },
                            gender: [],
                            state: [],
                            locality: [],
                            interests: [],
                            intention: [],
                            sentiment: "",
                            interactions: { operator: "", from: "", to: "" },
                            lastModifiedDate: { type: "", from: "", to: "" },
                        },
                        segOperators: { gender: false, state: false, locality: false, interests: false, intention: false },
                        segmentPreview: null,
                        segPage: 1,
                    }));
                    fetchSegmentPreview({
                        age: { operator: "", ageFrom: "", ageTo: "" },
                        gender: [],
                        state: [],
                        locality: [],
                        interests: [],
                        intention: [],
                        sentiment: "",
                        interactions: { operator: "", from: "", to: "" },
                        lastModifiedDate: { type: "", from: "", to: "" },
                    }, 1);
                }}
                onSave={() => {
                    console.log("segmentationFilters saved");
                    updateContextAttribute("alert", {
                        open: true,
                        severity: "success",
                        message: "Segmentación guardada correctamente",
                    });
                    backToNewCampaign();
                }}
                saveButtonName="Guardar"
                extraButtons={
                    <MuiButton
                        disabled={!hasSegmentationFilters()}
                        onClick={() => {
                            console.log("Segmentar clicked, filters:", JSON.stringify(segFilters));
                            handleCalculateSegmentation();
                        }}
                        variant="contained"
                        style={{
                            backgroundColor: hasSegmentationFilters() ? "#44AB3F" : "#B2B2B2",
                            color: "white",
                            borderRadius: 25,
                            textTransform: "none",
                        }}
                    >
                        Segmentar
                    </MuiButton>
                }
            />
        </React.Fragment>
    );

    // ==================== RENDER SCHEDULING VIEW ====================
    const today = new Date();
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 14);
    const todayStr = today.toISOString().split("T")[0];
    const maxDateStr = maxDate.toISOString().split("T")[0];

    const weekDays = segCatalogOptions.daysOfWeek.length > 0
        ? segCatalogOptions.daysOfWeek
        : [
            { value: "MONDAY", label: "Lunes" },
            { value: "TUESDAY", label: "Martes" },
            { value: "WEDNESDAY", label: "Miércoles" },
            { value: "THURSDAY", label: "Jueves" },
            { value: "FRIDAY", label: "Viernes" },
            { value: "SATURDAY", label: "Sábado" },
            { value: "SUNDAY", label: "Domingo" },
        ];

    const handleStartDateChange = (value) => {
        let newState = { startDate: value, errors: { ...state.errors, startDate: value === "" } };
        // Si la fecha de inicio supera la fecha final, limpiar fecha final
        if (endDate && value > endDate) {
            newState.endDate = "";
        }
        setState((prev) => ({ ...prev, ...newState }));
    };

    const handleStartTimeChange = (value) => {
        setState((prev) => ({
            ...prev,
            startTime: value,
            errors: { ...prev.errors, startTime: value === "" },
        }));
    };

    const handleRecurrentChange = (checked) => {
        setState((prev) => ({
            ...prev,
            recurrent: checked,
            frequency: "DAILY",
            endDate: "",
            daysOfWeek: [],
            sendOnce: false,
            errors: { ...prev.errors, endDate: false, daysOfWeek: false },
        }));
    };

    const handleFrequencyChange = (value) => {
        setState((prev) => ({
            ...prev,
            frequency: value,
            daysOfWeek: [],
            errors: { ...prev.errors, daysOfWeek: false },
        }));
    };

    const handleEndDateChange = (value) => {
        setState((prev) => ({
            ...prev,
            endDate: value,
            errors: { ...prev.errors, endDate: value === "" || value <= startDate },
        }));
    };

    const toggleDayOfWeek = (day) => {
        let newDays = [...daysOfWeek];
        if (newDays.includes(day)) {
            newDays = newDays.filter((d) => d !== day);
        } else {
            newDays.push(day);
        }
        setState((prev) => ({
            ...prev,
            daysOfWeek: newDays,
            errors: { ...prev.errors, daysOfWeek: newDays.length === 0 },
        }));
    };

    const renderScheduling = () => (
        <React.Fragment>
            <Grid container justifyContent="space-around">
                <Grid item lg={7} md={9} sm={12} xs={12}>
                    {/* Fecha de inicio */}
                    <Grid container spacing={3} alignItems="flex-start" justifyContent="center">
                        <Grid item sm={12} xs={12}>
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid item sm={3} xs={12}>
                                    <label style={{ textAlign: "right", display: "block" }}>
                                        <span style={{ color: "red" }}>* </span>
                                        Fecha de inicio
                                        <Tooltip title="Selecciona una fecha para el inicio de la campaña.">
                                            <HelpOutlineIcon color="primary" fontSize="small" style={{ verticalAlign: "middle", marginLeft: "4px", fontSize: "1rem" }} />
                                        </Tooltip>
                                    </label>
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <TextField
                                        type="date"
                                        name="startDate"
                                        value={startDate}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ min: todayStr, max: maxDateStr }}
                                        error={errors.startDate}
                                        helperText={errors.startDate ? "* Selecciona una fecha válida." : "La campaña puede programarse únicamente dentro de los próximos 14 días."}
                                        onChange={(e) => handleStartDateChange(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Hora de inicio */}
                    <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                        <Grid item sm={12} xs={12}>
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid item sm={3} xs={12}>
                                    <label style={{ textAlign: "right", display: "block" }}>
                                        <span style={{ color: "red" }}>* </span>
                                        Hora de inicio
                                        <Tooltip title="Selecciona la hora en que comenzará el envío.">
                                            <HelpOutlineIcon color="primary" fontSize="small" style={{ verticalAlign: "middle", marginLeft: "4px", fontSize: "1rem" }} />
                                        </Tooltip>
                                    </label>
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <TextField
                                        type="time"
                                        name="startTime"
                                        value={startTime}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        error={errors.startTime}
                                        helperText={errors.startTime ? "* Selecciona una hora válida." : ""}
                                        onChange={(e) => handleStartTimeChange(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Checkbox recurrencia */}
                    <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                        <Grid item sm={12} xs={12}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item sm={3} xs={12}></Grid>
                                <Grid item sm={6} xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={recurrent}
                                                onChange={(e) => handleRecurrentChange(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <span>
                                                Enviar recurrentemente
                                                <Tooltip title="Activa esta opción para repetir el envío automáticamente.">
                                                    <HelpOutlineIcon color="primary" fontSize="small" style={{ verticalAlign: "middle", marginLeft: "4px", fontSize: "1rem" }} />
                                                </Tooltip>
                                            </span>
                                        }
                                    />
                                    {recurrent && (
                                        <FormHelperText style={{ marginLeft: "14px", marginTop: "8px" }}>
                                            La campaña se enviará automáticamente según la frecuencia y los días seleccionados.
                                        </FormHelperText>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Campos de recurrencia */}
                    {recurrent && (
                        <React.Fragment>
                            {/* Frecuencia */}
                            <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                                <Grid item sm={12} xs={12}>
                                    <Grid container spacing={2} alignItems="flex-start">
                                        <Grid item sm={3} xs={12}>
                                            <label style={{ textAlign: "right", display: "block" }}>
                                                <span style={{ color: "red" }}>* </span>
                                                Frecuencia
                                                <Tooltip title="Selecciona con qué frecuencia se repetirá el envío de la campaña.">
                                                    <HelpOutlineIcon color="primary" fontSize="small" style={{ verticalAlign: "middle", marginLeft: "4px", fontSize: "1rem" }} />
                                                </Tooltip>
                                            </label>
                                        </Grid>
                                        <Grid item sm={6} xs={12}>
                                            <TextField
                                                select
                                                name="frequency"
                                                value={frequency}
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                onChange={(e) => handleFrequencyChange(e.target.value)}
                                            >
                                                <MenuItem value="DAILY">Diaria</MenuItem>
                                                <MenuItem value="WEEKLY">Semanal</MenuItem>
                                            </TextField>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Días de la semana (solo si frecuencia es semanal) — debajo de Frecuencia */}
                            {frequency === "WEEKLY" && (
                                <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                                    <Grid item sm={12} xs={12}>
                                        <Grid container spacing={2} alignItems="flex-start">
                                            <Grid item sm={3} xs={12}>
                                                <label style={{ textAlign: "right", display: "block" }}>
                                                    <span style={{ color: "red" }}>* </span>
                                                    Días de ejecución
                                                </label>
                                            </Grid>
                                            <Grid item sm={6} xs={12}>
                                                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                                                    {weekDays.map((day) => {
                                                        const initial = day.label.charAt(0);
                                                        const isSelected = daysOfWeek.includes(day.value);
                                                        return (
                                                            <div
                                                                key={day.value}
                                                                onClick={() => toggleDayOfWeek(day.value)}
                                                                style={{
                                                                    width: "36px",
                                                                    height: "36px",
                                                                    borderRadius: "50%",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    cursor: "pointer",
                                                                    backgroundColor: isSelected ? "#27315d" : "#e0e0e0",
                                                                    color: isSelected ? "white" : "#333",
                                                                    fontSize: "0.85rem",
                                                                    fontWeight: "500",
                                                                    userSelect: "none",
                                                                }}
                                                                title={day.label}
                                                            >
                                                                {initial}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {errors.daysOfWeek && (
                                                    <span style={{ color: "#f44336", fontSize: "0.75rem", marginTop: "5px", display: "block" }}>
                                                        * Debe seleccionar al menos un día de la semana.
                                                    </span>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}

                            {/* Finaliza el */}
                            <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                                <Grid item sm={12} xs={12}>
                                    <Grid container spacing={2} alignItems="flex-start">
                                        <Grid item sm={3} xs={12}>
                                            <label style={{ textAlign: "right", display: "block" }}>
                                                <span style={{ color: "red" }}>* </span>
                                                Finaliza el
                                                <Tooltip title="Selecciona hasta qué fecha deseas mantener la ejecución de la campaña.">
                                                    <HelpOutlineIcon color="primary" fontSize="small" style={{ verticalAlign: "middle", marginLeft: "4px", fontSize: "1rem" }} />
                                                </Tooltip>
                                            </label>
                                        </Grid>
                                        <Grid item sm={6} xs={12}>
                                            <TextField
                                                type="date"
                                                name="endDate"
                                                value={endDate}
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ min: startDate || todayStr }}
                                                error={errors.endDate}
                                                helperText={errors.endDate ? "* La fecha debe ser posterior a la fecha de inicio." : ""}
                                                onChange={(e) => handleEndDateChange(e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Checkbox Envío único */}
                            <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                                <Grid item sm={12} xs={12}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item sm={3} xs={12}></Grid>
                                        <Grid item sm={6} xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={sendOnce}
                                                        onChange={(e) => setState((prev) => ({ ...prev, sendOnce: e.target.checked }))}
                                                        color="primary"
                                                    />
                                                }
                                                label={
                                                    <span>
                                                        Enviar solo a contactos nuevos
                                                        <Tooltip title="Excluye a los contactos que ya recibieron la campaña en envíos anteriores.">
                                                            <HelpOutlineIcon color="primary" fontSize="small" style={{ verticalAlign: "middle", marginLeft: "4px", fontSize: "1rem" }} />
                                                        </Tooltip>
                                                    </span>
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </React.Fragment>
                    )}
                </Grid>
            </Grid>

            <FooterControls
                disableCancel={!hasUnsavedSchedulingChanges()}
                disableSave={
                    !startDate || !startTime ||
                    (recurrent && (!endDate || endDate <= startDate)) ||
                    (recurrent && frequency === "WEEKLY" && daysOfWeek.length === 0)
                }
                saveTitle="¿Deseas guardar la programación?"
                saveMessage="Se aplicará la fecha y hora configuradas a la campaña."
                onCancel={() => clearSchedulingFields()}
                onSave={() => {
                    let scheduleData = {
                        sending: "SCHEDULED",
                        recurrent: recurrent,
                        startDate: startDate,
                        startTime: startTime,
                    };
                    if (recurrent) {
                        scheduleData.frequency = frequency;
                        scheduleData.endDate = endDate;
                        if (frequency === "WEEKLY") {
                            scheduleData.daysOfWeek = daysOfWeek;
                        }
                    }
                    console.log("scheduleData:", scheduleData);
                    updateContextAttribute("alert", {
                        open: true,
                        severity: "success",
                        message: "Programación guardada correctamente",
                    });
                    backToNewCampaign();
                }}
                saveButtonName="Guardar"
            />
        </React.Fragment>
    );

    // ==================== RENDER CAMPAIGN DETAIL VIEW ====================
    const renderCampaignDetail = () => {
        if (!selectedCampaign) return null;
        const rules = campaignStateRules[selectedCampaign.state] || {};
        const isActive = selectedCampaign.state === "ACTIVE";
        const isInactive = selectedCampaign.state === "INACTIVE";
        const canToggle = rules.canActivate || rules.canDeactivate;
        const isReadOnly = selectedCampaign.state === "EXECUTED" || selectedCampaign.state === "DELETE";
        const deliveryLabel = catalogTranslations.Campaign_Delivery?.[selectedCampaign.delivery] || selectedCampaign.delivery || "";
        const sendingLabel = catalogTranslations.Campaign_Sending?.[selectedCampaign.sending] || selectedCampaign.sending || "";

        return (
            <React.Fragment>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <h3>Detalle de campaña</h3>
                    <IconButton onClick={() => confirmExit(true, backToList)}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <Grid container justifyContent="space-around">
                    <Grid item lg={10} md={11} sm={12} xs={12}>
                        {/* Nombre + Estado */}
                        <Grid container spacing={3} alignItems="flex-start" justifyContent="center">
                            <Grid item sm={12} xs={12}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item sm={3} xs={12}>
                                        <label style={{ textAlign: "right", display: "block" }}>
                                            Nombre de la Campaña
                                        </label>
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <span style={{ fontWeight: "500" }}>{selectedCampaign.name}</span>
                                        <span style={{ marginLeft: "15px", color: getStateColor(selectedCampaign.state), fontWeight: "500" }}>
                                            ({getStateLabel(selectedCampaign.state)})
                                        </span>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Template */}
                        <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                            <Grid item sm={12} xs={12}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item sm={3} xs={12}>
                                        <label style={{ textAlign: "right", display: "block" }}>Plantilla</label>
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <span>{selectedCampaign.template || ""}</span>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Tipo */}
                        <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                            <Grid item sm={12} xs={12}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item sm={3} xs={12}>
                                        <label style={{ textAlign: "right", display: "block" }}>Tipo</label>
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <span>{sendingLabel}</span>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Origen */}
                        <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                            <Grid item sm={12} xs={12}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item sm={3} xs={12}>
                                        <label style={{ textAlign: "right", display: "block" }}>Enviada a</label>
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <span>{deliveryLabel}</span>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Fecha de creación */}
                        <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                            <Grid item sm={12} xs={12}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item sm={3} xs={12}>
                                        <label style={{ textAlign: "right", display: "block" }}>Fecha de creación</label>
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <span>{selectedCampaign.date || ""}</span>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Usuario creador */}
                        <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                            <Grid item sm={12} xs={12}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item sm={3} xs={12}>
                                        <label style={{ textAlign: "right", display: "block" }}>Usuario creador</label>
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <span>{selectedCampaign.user || ""}</span>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Detalle de programación (solo lectura) - no mostrar para ACTIVE */}
                        {selectedCampaign.schedule && !isActive && (
                            <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "20px" }}>
                                <Grid item sm={12} xs={12}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item sm={3} xs={12}>
                                            <label style={{ textAlign: "right", display: "block", fontWeight: "500" }}>Programación</label>
                                        </Grid>
                                        <Grid item sm={6} xs={12}>
                                            <div>
                                                <p style={{ margin: "4px 0" }}><strong>Tipo:</strong> {selectedCampaign.schedule.type === "RECURRING" ? "Recurrente" : "Una sola ejecución"}</p>
                                                <p style={{ margin: "4px 0" }}><strong>Fecha inicio:</strong> {startDate || "-- --"}</p>
                                                <p style={{ margin: "4px 0" }}><strong>Hora inicio:</strong> {startTime || "-- --"}</p>
                                                {recurrent && <p style={{ margin: "4px 0" }}><strong>Frecuencia:</strong> {frequency === "WEEKLY" ? "Semanal" : "Diaria"}</p>}
                                                {recurrent && endDate && <p style={{ margin: "4px 0" }}><strong>Fecha fin:</strong> {endDate}</p>}
                                                {recurrent && frequency === "WEEKLY" && daysOfWeek.length > 0 && (
                                                    <div style={{ display: "flex", gap: "6px", marginTop: "8px" }}>
                                                        {weekDays.map((day) => (
                                                            <div key={day.value} style={{ width: "28px", height: "28px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: daysOfWeek.includes(day.value) ? "#27315d" : "#e0e0e0", color: daysOfWeek.includes(day.value) ? "white" : "#333", fontSize: "0.7rem", fontWeight: "500" }} title={day.label}>{day.label.charAt(0)}</div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}

                        {/* Switch Activar/Desactivar - solo para ACTIVE/INACTIVE */}
                        {(isActive || isInactive) && (<React.Fragment>
                            <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "20px" }}>
                            <Grid item sm={12} xs={12}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item sm={3} xs={12}>
                                        <label style={{ textAlign: "right", display: "block" }}>
                                            {switchActive ? "Desactivar campaña" : "Activar campaña"}
                                        </label>
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <Switch
                                            checked={switchActive}
                                            disabled={!canToggle}
                                            color="primary"
                                            onChange={async () => {
                                                if (switchActive) {
                                                    // Desactivar: confirmar y cambiar visualmente
                                                    let result = await Swal.fire({
                                                        title: "Se desactivará la campaña, ¿deseas continuar?",
                                                        showDenyButton: true,
                                                        confirmButtonText: "Sí",
                                                        denyButtonText: "No",
                                                        confirmButtonColor: "#27315d",
                                                        denyButtonColor: "#27315d",
                                                    });
                                                    if (result.isConfirmed) {
                                                        setState((prev) => ({
                                                            ...prev,
                                                            switchActive: false,
                                                        }));
                                                    }
                                                } else {
                                                    // Activar: solo habilitar botón guardar
                                                    setState((prev) => ({
                                                        ...prev,
                                                        switchActive: true,
                                                    }));
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Checkbox Modificar Campaña / Sección de programación */}
                        {(isActive && selectedCampaign.sending === "SCHEDULED") && (
                            <React.Fragment>
                                {/* Botón Modificar Programación */}
                                <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px" }}>
                                    <Grid item sm={12} xs={12}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item sm={3} xs={12}>
                                                <label style={{ textAlign: "right", display: "block" }}>
                                                    Modificar Programación
                                                </label>
                                            </Grid>
                                            <Grid item sm={6} xs={12}>
                                                <Switch
                                                    checked={editingSchedule}
                                                    color="primary"
                                                    onChange={(e) => {
                                                        setState((prev) => ({ ...prev, editingSchedule: e.target.checked }));
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Campos de programación */}
                                <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "15px", opacity: editingSchedule ? 1 : 0.6 }}>
                                    <Grid item sm={12} xs={12}>
                                        <Grid container spacing={2} alignItems="flex-start">
                                            <Grid item sm={3} xs={12}>
                                                <label style={{ textAlign: "right", display: "block" }}>
                                                    <span style={{ color: "red" }}>* </span>
                                                    Fecha de inicio
                                                </label>
                                            </Grid>
                                            <Grid item sm={3} xs={12}>
                                                <TextField
                                                    type="date"
                                                    value={startDate}
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={!editingSchedule}
                                                    InputLabelProps={{ shrink: true }}
                                                    inputProps={{ min: todayStr, max: maxDateStr }}
                                                    onChange={(e) => handleStartDateChange(e.target.value)}
                                                    style={{ maxWidth: "180px" }}
                                                />
                                            </Grid>
                                            <Grid item sm={3} xs={12}>
                                                <label style={{ textAlign: "right", display: "block" }}>
                                                    <span style={{ color: "red" }}>* </span>
                                                    Hora de inicio
                                                </label>
                                            </Grid>
                                            <Grid item sm={3} xs={12}>
                                                <TextField
                                                    type="time"
                                                    value={startTime}
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={!editingSchedule}
                                                    InputLabelProps={{ shrink: true }}
                                                    onChange={(e) => handleStartTimeChange(e.target.value)}
                                                    style={{ maxWidth: "150px" }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Checkbox recurrencia */}
                                <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px", opacity: editingSchedule ? 1 : 0.6 }}>
                                    <Grid item sm={12} xs={12}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item sm={3} xs={12}></Grid>
                                            <Grid item sm={6} xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={recurrent}
                                                            disabled={!editingSchedule}
                                                            onChange={(e) => handleRecurrentChange(e.target.checked)}
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Enviar recurrentemente"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Frecuencia y Fecha fin */}
                                {recurrent && (
                                    <React.Fragment>
                                        <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px", opacity: editingSchedule ? 1 : 0.6 }}>
                                            <Grid item sm={12} xs={12}>
                                                <Grid container spacing={2} alignItems="flex-start">
                                                    <Grid item sm={3} xs={12}>
                                                        <label style={{ textAlign: "right", display: "block" }}>
                                                            <span style={{ color: "red" }}>* </span>
                                                            Frecuencia
                                                        </label>
                                                    </Grid>
                                                    <Grid item sm={3} xs={12}>
                                                        <TextField
                                                            select
                                                            value={frequency}
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={!editingSchedule}
                                                            onChange={(e) => handleFrequencyChange(e.target.value)}
                                                            style={{ maxWidth: "180px", width: "100%" }}
                                                        >
                                                            <MenuItem value="DAILY">Diaria</MenuItem>
                                                            <MenuItem value="WEEKLY">Semanal</MenuItem>
                                                        </TextField>
                                                    </Grid>
                                                    <Grid item sm={3} xs={12}>
                                                        <label style={{ textAlign: "right", display: "block" }}>
                                                            <span style={{ color: "red" }}>* </span>
                                                            Finaliza el
                                                        </label>
                                                    </Grid>
                                                    <Grid item sm={3} xs={12}>
                                                        <TextField
                                                            type="date"
                                                            value={endDate}
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={!editingSchedule}
                                                            InputLabelProps={{ shrink: true }}
                                                            inputProps={{ min: startDate || todayStr }}
                                                            onChange={(e) => handleEndDateChange(e.target.value)}
                                                            style={{ maxWidth: "180px" }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        {/* Días de la semana (solo frecuencia semanal) */}
                                        {frequency === "WEEKLY" && (
                                            <Grid container spacing={3} alignItems="flex-start" justifyContent="center" style={{ marginTop: "10px", opacity: editingSchedule ? 1 : 0.6 }}>
                                                <Grid item sm={12} xs={12}>
                                                    <Grid container spacing={2} alignItems="flex-start">
                                                        <Grid item sm={3} xs={12}>
                                                            <label style={{ textAlign: "right", display: "block" }}>
                                                                <span style={{ color: "red" }}>* </span>
                                                                Días de ejecución
                                                            </label>
                                                        </Grid>
                                                        <Grid item sm={6} xs={12}>
                                                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                                                                {weekDays.map((day) => {
                                                                    const initial = day.label.charAt(0);
                                                                    const isSelected = daysOfWeek.includes(day.value);
                                                                    return (
                                                                        <div
                                                                            key={day.value}
                                                                            onClick={() => editingSchedule && toggleDayOfWeek(day.value)}
                                                                            style={{
                                                                                width: "36px",
                                                                                height: "36px",
                                                                                borderRadius: "50%",
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                cursor: editingSchedule ? "pointer" : "default",
                                                                                backgroundColor: isSelected ? "#27315d" : "#e0e0e0",
                                                                                color: isSelected ? "white" : "#333",
                                                                                fontSize: "0.85rem",
                                                                                fontWeight: "500",
                                                                                userSelect: "none",
                                                                                opacity: editingSchedule ? 1 : 0.6,
                                                                            }}
                                                                            title={day.label}
                                                                        >
                                                                            {initial}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        )}

                        <FooterControls
                            disableCancel={!editingSchedule && switchActive === (selectedCampaign.state === "ACTIVE")}
                            disableSave={!editingSchedule && switchActive === (selectedCampaign.state === "ACTIVE")}
                            saveTitle="¿Deseas aplicar los cambios a la campaña?"
                            saveMessage="Se guardarán las modificaciones realizadas en la campaña."
                            onCancel={() => {
                                setState((prev) => ({
                                    ...prev,
                                    editingSchedule: false,
                                    switchActive: selectedCampaign.state === "ACTIVE",
                                }));
                            }}
                            onSave={async () => {
                                const isActivating = isInactive && switchActive;
                                const isDeactivating = isActive && !switchActive;
                                updateContextAttribute("loadingDialog", true);
                                updateContextAttribute("LoadingMessage", "Procesando...");
                                let res;
                                if (isActivating) {
                                    res = await activateCampaign(selectedCampaign.campaignId);
                                } else if (isDeactivating) {
                                    res = await deactivateCampaign(selectedCampaign.campaignId);
                                } else {
                                    let schedule;
                                    if (recurrent) {
                                        schedule = {
                                            type: "RECURRING",
                                            frequency: frequency,
                                            daysOfWeek: frequency === "WEEKLY" ? daysOfWeek : undefined,
                                            time: startTime,
                                            startDate: startDate,
                                            endDate: endDate,
                                        };
                                    } else {
                                        schedule = {
                                            type: "ONE_TIME",
                                            executionDateTime: `${startDate}T${startTime}:00`,
                                        };
                                    }
                                    res = await updateCampaignSchedule(selectedCampaign.campaignId, schedule);
                                }
                                updateContextAttribute("loadingDialog", false);
                                updateContextAttribute("LoadingMessage", "");
                                if (res?.status === 200 || res?.status === 201) {
                                    updateContextAttribute("alert", {
                                        open: true,
                                        severity: "success",
                                        message: isActivating
                                            ? "Campaña activada correctamente"
                                            : isDeactivating
                                            ? "Campaña desactivada correctamente"
                                            : "Programación actualizada correctamente",
                                    });
                                    fetchCampaignList();
                                    backToList();
                                } else {
                                    Swal.fire({
                                        icon: "error",
                                        title: "Error",
                                        text: res?.data?.response?.message || res?.data?.error || res?.response?.data?.response?.message || res?.response?.data?.error || "Intenta más tarde.",
                                    });
                                }
                            }}
                            saveButtonName="Guardar"
                            extraButtons={
                                (selectedCampaign.state === "ACTIVE" || selectedCampaign.state === "EXECUTED") && (
                                    <CustomButton
                                        onClick={async () => {
                                            let result = await Swal.fire({
                                                title: "Se eliminará la campaña, ¿estás seguro de continuar?",
                                                showDenyButton: true,
                                                confirmButtonText: "Sí",
                                                denyButtonText: "No",
                                                confirmButtonColor: "#27315d",
                                                denyButtonColor: "#27315d",
                                            });
                                            if (result.isConfirmed) {
                                                updateContextAttribute("loadingDialog", true);
                                                updateContextAttribute("LoadingMessage", "Eliminando campaña...");
                                                let res = await deleteCampaign(selectedCampaign.campaignId);
                                                updateContextAttribute("loadingDialog", false);
                                                updateContextAttribute("LoadingMessage", "");
                                                if (res?.status === 200 || res?.status === 201) {
                                                    updateContextAttribute("alert", {
                                                        open: true,
                                                        severity: "success",
                                                        message: "Campaña eliminada correctamente",
                                                    });
                                                    fetchCampaignList();
                                                    backToList();
                                                } else {
                                                    Swal.fire({
                                                        icon: "error",
                                                        title: "Error",
                                                        text: res?.data?.response?.message || res?.data?.error || "No se pudo eliminar la campaña.",
                                                    });
                                                }
                                            }
                                        }}
                                        variant="contained"
                                        style={{ backgroundColor: "#666666" }}
                                    >
                                        Eliminar
                                    </CustomButton>
                                )
                            }
                        />
                        </React.Fragment>
                        )}

                        {/* Botón eliminar solo para campañas EXECUTED (no DELETE) */}
                        {isReadOnly && selectedCampaign.state !== "DELETE" && (
                            <FooterControls
                                disableCancel={true}
                                disableSave={true}
                                noCancel={true}
                                onCancel={() => {}}
                                onSave={() => {}}
                                extraButtons={
                                    <CustomButton
                                        onClick={async () => {
                                            let result = await Swal.fire({
                                                title: "Se eliminará la campaña, ¿estás seguro de continuar?",
                                                showDenyButton: true,
                                                confirmButtonText: "Sí",
                                                denyButtonText: "No",
                                                confirmButtonColor: "#27315d",
                                                denyButtonColor: "#27315d",
                                            });
                                            if (result.isConfirmed) {
                                                updateContextAttribute("loadingDialog", true);
                                                updateContextAttribute("LoadingMessage", "Eliminando campaña...");
                                                let res = await deleteCampaign(selectedCampaign.campaignId);
                                                updateContextAttribute("loadingDialog", false);
                                                updateContextAttribute("LoadingMessage", "");
                                                if (res?.status === 200 || res?.status === 201) {
                                                    updateContextAttribute("alert", {
                                                        open: true,
                                                        severity: "success",
                                                        message: "Campaña eliminada correctamente",
                                                    });
                                                    fetchCampaignList();
                                                    backToList();
                                                } else {
                                                    Swal.fire({
                                                        icon: "error",
                                                        title: "Error",
                                                        text: res?.data?.response?.message || res?.data?.error || "No se pudo eliminar la campaña.",
                                                    });
                                                }
                                            }
                                        }}
                                        variant="contained"
                                        style={{ backgroundColor: "#666666" }}
                                    >
                                        Eliminar
                                    </CustomButton>
                                }
                            />
                        )}
                    </Grid>
                </Grid>
            </React.Fragment>
        );
    };

    // ==================== RENDER SELECT TEMPLATE VIEW ====================
    const searchTemplates = (query) => {
        setState((prev) => ({ ...prev, templateSearchQuery: query }));
    };

    const getFilteredTemplates = () => {
        if (!templateSearchQuery) return templateFullList;
        return templateFullList.filter((t) =>
            t.templateName.toLowerCase().includes(templateSearchQuery.toLowerCase())
        );
    };

    const renderSelectTemplate = () => {
        const filteredTemplates = getFilteredTemplates();
        return (
        <React.Fragment>
            <CreateNewTemplateForm
                allTemplatesNames={templateList}
                handleOpenNewTemplate={handleOpenNewTemplateForm}
                openNewTemplateForm={openNewTemplateForm}
                refreshTemplateList={refreshTemplateListForCampaign}
                handleDialogAction={handleTemplateDialogAction}
            />
            <CustomModalActions dialog={templateDialog} handleDialogAction={handleTemplateDialogAction} />
            <div style={{ display: "flex", alignItems: "center", marginBottom: "15px", maxWidth: "800px", margin: "0 auto 15px auto" }}>
                <SearchBox searchFunction={searchTemplates} searchQuery={templateSearchQuery} />
                <RefreshButton refreshFunction={refreshTemplateListForCampaign} />
            </div>
            <div style={{ maxWidth: "800px", margin: "0 auto", paddingBottom: "60px" }}>
                {loadingTemplates ? (
                    <div className="center-loading-icon">
                        <CircularProgress />
                    </div>
                ) : filteredTemplates.length > 0 ? (
                    filteredTemplates.map((template, index) => (
                        <div key={index}>
                            <div className="template-header">
                                <h3>{template.templateName}</h3>
                                <Tooltip title={
                                    template.templateStatus === "APPROVED" ? "Lista para usar"
                                    : template.templateStatus === "REJECTED" ? "Rechazada por WhatsApp"
                                    : "Pendiente de revisión"
                                }>
                                    <span style={{
                                        width: "12px",
                                        height: "12px",
                                        borderRadius: "50%",
                                        display: "inline-block",
                                        backgroundColor: template.templateStatus === "APPROVED" ? "#44AB3F"
                                            : template.templateStatus === "REJECTED" ? "#E42922"
                                            : "#e48922",
                                    }} />
                                </Tooltip>
                            </div>
                            <div className="template-item">
                                <RadioGroup
                                    row
                                    value={selectedTemplate}
                                    onChange={(e) => handleSelectTemplateFromList(e.target.value)}
                                >
                                    <FormControlLabel
                                        value={template.templateName}
                                        disabled={template.templateStatus !== "APPROVED"}
                                        control={<Radio color="primary" />}
                                    />
                                </RadioGroup>
                                <div className="multimedia-template-container gray-input">
                                    <TextField
                                        className="gray-input"
                                        disabled
                                        value={template.templateContent}
                                        size="small"
                                        fullWidth
                                        multiline
                                        variant="outlined"
                                    />
                                    {template.templateButtons && template.templateButtons.length > 0 && (
                                        <div className="template-buttons-container">
                                            <label className="top-label-field">Botones</label>
                                            {template.templateButtons.map((button, btnIndex) => (
                                                <div key={btnIndex} className="template-button">
                                                    <TextField
                                                        className="gray-input"
                                                        disabled
                                                        value={button.text}
                                                        size="small"
                                                        fullWidth
                                                        variant="outlined"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={{ textAlign: "center", marginTop: "40px" }}>
                        <span>No hay plantillas disponibles.</span>
                    </div>
                )}
            </div>
            {/* Footer: solo visible cuando la lista ya cargó */}
            {!loadingTemplates && (
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    zIndex: 999,
                    backgroundColor: "#fff",
                    width: "100%",
                    padding: "12px 16px",
                    alignItems: "center",
                    borderTop: "none",
                }}>
                    <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
                        <CustomButton
                            disabled={!selectedTemplate}
                            onClick={confirmSelectTemplate}
                            variant="contained"
                        >
                            Seleccionar Plantilla
                        </CustomButton>
                    </div>
                    <div style={{ marginLeft: "auto" }}>
                        <MuiButton
                            color="primary"
                            size="small"
                            startIcon={<AgregarIcono width="15px" height="15px" />}
                            onClick={() => handleOpenNewTemplateForm(true)}
                            style={{ textTransform: "none" }}
                        >
                            Crear Plantilla
                        </MuiButton>
                    </div>
                </div>
            )}
        </React.Fragment>
        );
    };

    // ==================== EMBEDDED MODE (inline in page) ====================
    if (embedded) {
        return (
            <div>
                {/* Dialog de confirmación para salir */}
                <MuiDialog
                    open={confirmDialog.open}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <MuiDialogTitle id="alert-dialog-title">
                        ¿Estás seguro que deseas salir?
                    </MuiDialogTitle>
                    <MuiDialogContent>Los cambios realizados se perderán.</MuiDialogContent>
                    <MuiDialogActions>
                        <CustomButton
                            onClick={() => setConfirmDialog({ open: false, onConfirm: null })}
                            variant="contained"
                        >
                            No
                        </CustomButton>
                        <CustomButton
                            onClick={() => {
                                setConfirmDialog({ open: false, onConfirm: null });
                                if (confirmDialog.onConfirm) confirmDialog.onConfirm();
                            }}
                            variant="contained"
                            color="primary"
                            autoFocus
                        >
                            Sí
                        </CustomButton>
                    </MuiDialogActions>
                </MuiDialog>

                {section === "list" && renderListView()}
                {section === "campaignDetail" && renderCampaignDetail()}

                {/* Dialog fullScreen para creación de campaña */}
                <Dialog open={section === "newCampaign" || section === "segmentation" || section === "scheduling" || section === "selectTemplate"} fullScreen>
                    <DialogTitle disableTypography>
                        <h2>
                            Creación de campaña
                            {section === "segmentation" && <span style={{ fontWeight: "bold" }}> &gt; Segmentación</span>}
                            {section === "scheduling" && <span style={{ fontWeight: "bold" }}> &gt; Programación</span>}
                            {section === "selectTemplate" && <span style={{ fontWeight: "bold" }}> &gt; Seleccionar Plantilla</span>}
                        </h2>
                        <IconButton onClick={() => {
                            if (section === "segmentation") {
                                confirmExit(hasSegmentationFilters(), cancelSegmentation);
                            } else if (section === "scheduling") {
                                confirmExit(hasUnsavedSchedulingChanges(), cancelScheduling);
                            } else {
                                confirmExit(hasNewCampaignChanges(), backToList);
                            }
                        }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        {section === "newCampaign" && renderNewCampaignForm()}
                        {section === "segmentation" && renderSegmentation()}
                        {section === "scheduling" && renderScheduling()}
                        {section === "selectTemplate" && renderSelectTemplate()}
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    // ==================== DIALOG MODE (fullscreen) ====================
    return (
        <div>
            {/* Dialog de confirmación para salir */}
            <MuiDialog
                open={confirmDialog.open}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <MuiDialogTitle id="alert-dialog-title">
                    ¿Estás seguro que deseas salir?
                </MuiDialogTitle>
                <MuiDialogContent>Los cambios realizados se perderán.</MuiDialogContent>
                <MuiDialogActions>
                    <CustomButton
                        onClick={() => setConfirmDialog({ open: false, onConfirm: null })}
                        variant="contained"
                    >
                        No
                    </CustomButton>
                    <CustomButton
                        onClick={() => {
                            setConfirmDialog({ open: false, onConfirm: null });
                            if (confirmDialog.onConfirm) confirmDialog.onConfirm();
                        }}
                        variant="contained"
                        color="primary"
                        autoFocus
                    >
                        Sí
                    </CustomButton>
                </MuiDialogActions>
            </MuiDialog>

            <Dialog open={openCampaignsDialog} fullScreen>
                <DialogTitle disableTypography>
                    {section === "list" ? (
                        <h2>Campañas</h2>
                    ) : section === "newCampaign" ? (
                        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                            <Tooltip title="Regresar">
                                <Button color="primary" size="small" onClick={() => confirmAndNavigate(backToList)} style={{ cursor: "pointer" }}>
                                    Campañas
                                </Button>
                            </Tooltip>
                            <Button color="primary" size="small" disabled>
                                Campaña: {campaignName || "Nueva Campaña"}
                            </Button>
                        </Breadcrumbs>
                    ) : section === "segmentation" ? (
                        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                            <Tooltip title="Regresar">
                                <Button color="primary" size="small" onClick={() => confirmAndNavigate(backToList)} style={{ cursor: "pointer" }}>
                                    Campañas
                                </Button>
                            </Tooltip>
                            <Tooltip title="Regresar">
                                <Button color="primary" size="small" onClick={() => confirmAndNavigate(backToNewCampaign)} style={{ cursor: "pointer" }}>
                                    Campaña: {campaignName || "Nueva Campaña"}
                                </Button>
                            </Tooltip>
                            <Button color="primary" size="small" disabled>
                                Segmentación
                            </Button>
                        </Breadcrumbs>
                    ) : section === "scheduling" ? (
                        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                            <Tooltip title="Regresar">
                                <Button color="primary" size="small" onClick={() => confirmAndNavigate(backToList)} style={{ cursor: "pointer" }}>
                                    Campañas
                                </Button>
                            </Tooltip>
                            <Tooltip title="Regresar">
                                <Button color="primary" size="small" onClick={() => confirmAndNavigate(backToNewCampaign)} style={{ cursor: "pointer" }}>
                                    Campaña: {campaignName || "Nueva Campaña"}
                                </Button>
                            </Tooltip>
                            <Button color="primary" size="small" disabled>
                                Programación
                            </Button>
                        </Breadcrumbs>
                    ) : section === "selectTemplate" ? (
                        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                            <Tooltip title="Regresar">
                                <Button color="primary" size="small" onClick={() => confirmAndNavigate(backToList)} style={{ cursor: "pointer" }}>
                                    Campañas
                                </Button>
                            </Tooltip>
                            <Tooltip title="Regresar">
                                <Button color="primary" size="small" onClick={() => confirmAndNavigate(backToNewCampaign)} style={{ cursor: "pointer" }}>
                                    Campaña: {campaignName || "Nueva Campaña"}
                                </Button>
                            </Tooltip>
                            <Button color="primary" size="small" disabled>
                                Seleccionar Template
                            </Button>
                        </Breadcrumbs>
                    ) : section === "campaignDetail" ? (
                        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                            <Tooltip title="Regresar">
                                <Button color="primary" size="small" onClick={() => backToList()} style={{ cursor: "pointer" }}>
                                    Campañas
                                </Button>
                            </Tooltip>
                            <Button color="primary" size="small" disabled>
                                {selectedCampaign?.name || "Detalle"}
                            </Button>
                        </Breadcrumbs>
                    ) : null}
                    <IconButton onClick={() => confirmExit(hasNewCampaignChanges() || hasUnsavedSchedulingChanges() || hasSegmentationFilters(), onClose)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {section === "list" && renderListView()}
                    {section === "newCampaign" && renderNewCampaignForm()}
                    {section === "segmentation" && renderSegmentation()}
                    {section === "scheduling" && renderScheduling()}
                    {section === "selectTemplate" && renderSelectTemplate()}
                    {section === "campaignDetail" && renderCampaignDetail()}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CampaignsDialog;
